import { useEffect, useState, type ReactNode } from 'react';
import Drawer from './Drawer.tsx';
import { api, errorMessage } from '../lib/api.ts';
import { OptionRow, OptionRows, Segmented, Toggle } from '../components/Controls.tsx';
import PeoplePanel from '../components/PeoplePanel.tsx';
import { EngineAccount } from './EngineAccount.tsx';
import { PiLogin } from './PiLogin.tsx';
import { PiProfiles } from './PiProfiles.tsx';
import type { PiProfile } from '../../shared/api.ts';
import { PiModelField } from './PiModelField.tsx';
import { useBuilds, killBuild, isActiveBuild, formatBuildAge } from '../lib/useBuilds.js';
// The rung tables' one client-side home — the drawer and the model pill narrow
// a ladder the same way, and each engine's projection matches its server twin
// (gatewayLadder / piEffortLadder).
import { gatewayRungs as gatewayRungsFor, piRungs } from '../lib/models.ts';
import { Check } from '../lib/icons.tsx';
import type {
  EngineInfo,
  CacheKind,
  CacheUsage,
  CacheUsagePayload,
  ClaudeResolvedModels,
  ClearCachePayload,
  GatewayModelsPayload,
  PiCatalogueModel,
  PiModelRefresh,
  SettingsPayload,
} from '../../shared/api.ts';

/** The alias probe, plus the local failure the drawer records when the request
 *  itself never landed — the panel renders one message either way. */
type ResolvedModels = ClaudeResolvedModels & { error?: string };

/** One engine as the drawer lists it: the payload's own block, plus the value
 *  and label this UI puts on it. */
type EngineRow = EngineInfo & { value: string; label: string };

const SECTION_KEY = 'studio.settings.section';

/**
 * WHICH SECTION IS ON SCREEN — one at a time, remembered across visits.
 *
 * This was an accordion until 2026-08-11, and the accordion was the problem:
 * eleven groups on one scroll, any of which could be open at once, and the Pi
 * one alone is longer than the drawer twice over. Opening it put you three
 * screens deep in a panel with nothing on screen saying which engine you were
 * editing — the head that said so had scrolled away, and the way back was to
 * scroll until you recognised something.
 *
 * A console fixes the orientation properly: the rail is always there, it says
 * where you are, and every section's current answer is readable WITHOUT
 * opening it, which is what the accordion's summary line was reaching for.
 */
function useSection(initial: string): [string, (id: string) => void] {
  const [id, setId] = useState<string>(() => {
    try {
      return localStorage.getItem(SECTION_KEY) || initial;
    } catch {
      return initial; // private mode — it just stops remembering
    }
  });
  function go(next: string): void {
    setId(next);
    try {
      localStorage.setItem(SECTION_KEY, next);
    } catch {
      /* ignore */
    }
  }
  return [id, go];
}

/** One section of the console. Rendered only while it is the active one, so
 *  the panels that fetch on mount (Storage, Usage, Running builds) still do
 *  exactly one read when you arrive at them. */
function Pane({
  id,
  name,
  now,
  active,
  children,
}: {
  id: string;
  name: string;
  /** The section's CURRENT ANSWER, repeated under its title so the pane says
   *  what the rail says — you can arrive here from either. */
  now?: ReactNode;
  active: string;
  children?: ReactNode;
}) {
  if (active !== id) return null;
  return (
    <section className="settings-pane" id={`settings-pane-${id}`} aria-labelledby={`settings-nav-${id}`}>
      <header className="settings-pane__head">
        <h3 className="settings-pane__title">{name}</h3>
        {now ? <p className="settings-pane__now">{now}</p> : null}
      </header>
      <div className="settings-pane__body">{children}</div>
    </section>
  );
}

/** One item in the navigation rail: the section's name, and under it the
 *  answer it currently holds. The `dot` is an ENGINE's state — offered, off,
 *  or not set up — and is deliberately absent on the sections that are not
 *  engines, so the mark means one thing wherever it appears. */
function NavItem({
  id,
  name,
  now,
  dot,
  active,
  onGo,
}: {
  id: string;
  name: string;
  now?: ReactNode;
  dot?: 'on' | 'off' | 'unset';
  active: string;
  onGo: (id: string) => void;
}) {
  const isActive = active === id;
  return (
    <button
      type="button"
      id={`settings-nav-${id}`}
      className={'settings-nav__item' + (isActive ? ' settings-nav__item--on' : '')}
      // The summary is one truncated line, so the whole of it is on hover
      // too — a rail that ends every engine in an ellipsis has to have
      // somewhere the rest of the sentence lives.
      title={typeof now === 'string' ? `${name} — ${now}` : name}
      aria-current={isActive ? 'true' : undefined}
      aria-controls={`settings-pane-${id}`}
      onClick={() => onGo(id)}
    >
      <span className="settings-nav__name">
        {dot ? <span className={`settings-nav__dot settings-nav__dot--${dot}`} aria-hidden="true" /> : null}
        {name}
      </span>
      {now ? <span className="settings-nav__now">{now}</span> : null}
    </button>
  );
}

/**
 * A band inside a section: ACCOUNT, BUILDS, CHAT.
 *
 * The three questions every engine answers, in the same order in every
 * section — whose credential it spends, what a BUILD runs on, what a MESSAGE
 * runs on. That order is the real shape of the config (auth is per engine,
 * model and effort are per surface), so the headings encode it rather than
 * decorating the scroll.
 */
function Band({ name, note, children }: { name: string; note?: ReactNode; children?: ReactNode }) {
  return (
    <section className="band">
      <h4 className="band__name">{name}</h4>
      {note ? <p className="band__note">{note}</p> : null}
      {children}
    </section>
  );
}

/**
 * Runtime settings. Chat runs on the Claude subscription by default; the one
 * optional extra is a metered OpenCode chat model, reached through the single
 * gateway preset (LLM_GATEWAY_*, any OpenAI-compatible endpoint). API keys stay
 * server-side env vars; the choices below persist in .studio-state and apply
 * from the next request, with no restart.
 */
/** A default model that survives Sonnet being switched off ('' = the Opus default). */
function dropSonnet(model: string | null | undefined): string {
  const m = (model || '').trim();
  return m === 'sonnet' ? '' : m;
}

/** A reasoning rung → words. The rungs differ per model (kimi-k2 goes one
 *  higher than deepseek-v4-flash), so the list is whatever the server says the
 *  picked model takes; this only decides how each one reads. */
const EFFORT_WORDS: Record<string, string> = {
  none: 'None — no thinking, fastest',
  off: 'Off — no thinking, fastest',
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
  ultra: 'Ultra — slowest, deepest',
};
function effortLabel(value: string): string {
  return EFFORT_WORDS[value] || value;
}

/**
 * What a Pi thinking dial means right now, in one sentence.
 *
 * It has to say WHOSE list is on offer, because that is the thing this row got
 * wrong until 2026-08-07: it claimed the rungs were the harness's and therefore
 * the same on every provider. Pi does own the seven flags, but it maps them
 * onto the endpoint's reasoning parameter, so on a model we hold rungs for the
 * list narrows to that model, and on one we do not it stays wide and the
 * endpoint is the judge. `off` is Pi's own either way: it sends no thinking.
 */
function piRungHint(offer: { efforts: string[]; known: boolean }, model: string, chosen: string, what: string): string {
  // The bare word, not the option label: the labels carry a trailing gloss
  // ("Off, no thinking, fastest") that reads as a stutter mid-sentence.
  const word = (rung: string) => ((EFFORT_WORDS[rung] || rung).split(/[,—]/)[0] || rung).trim().toLowerCase();
  const set = chosen ? `${what} at ${word(chosen)}.` : "No rung is sent, so the model's own default stands.";
  if (!offer.known) {
    return `${set} We hold no rung list for ${model || 'this model'}, so Pi's whole ladder is on offer and the endpoint decides which of them it honours.`;
  }
  const rungs = offer.efforts.filter((e) => e !== 'off');
  if (rungs.length === 0) {
    return `${set} ${model} reasons with no rung you can set, so the only choice here is Off, which sends no thinking at all.`;
  }
  return `${set} ${model} takes ${rungs.map(word).join(', ')}, plus Off, which sends no thinking at all.`;
}

export default function SettingsDrawer({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<SettingsPayload | null>(null);
  // Whether this server offers each engine at all — one switch at the top of
  // each engine's own group, because it outranks everything under it and takes
  // that engine off BUILDS AND CHAT together.
  const [claudeEnabled, setClaudeEnabled] = useState(true);
  const [fastEnabled, setFastEnabled] = useState(true);
  const [piEnabled, setPiEnabled] = useState(true);
  const [claudeModel, setClaudeModel] = useState(''); // chat + section builds
  // What a WHOLE-DECK Claude build runs on. Its own choice, so every engine's
  // build model is answered in the same place instead of Claude's being a
  // client-side constant. '' = the default (Opus).
  const [claudeBuildModel, setClaudeBuildModel] = useState('');
  // Is Sonnet offered at all? Off by default (2026-08-01) — see CHAT_MODELS in
  // lib/models.js for why the choice went away rather than the model.
  const [offerSonnet, setOfferSonnet] = useState(false);
  const [fillFloor, setFillFloor] = useState(0.4); // whitespace floor (0 = off)
  // Does a Pi build run the fresh-eyes render review? On by default; off, the
  // repair pass runs on the server's own checks alone, like the OpenCode engine.
  const [piReviewPass, setPiReviewPass] = useState(true);
  // May a Pi build draw inline SVG? Off by default: the slide rules say not
  // to, because the models that rule was written against drew crude shapes.
  const [piSvgArt, setPiSvgArt] = useState(false);
  // When the reviewing model is known to be text-only, does the server look at
  // every render with a vision model first and hand the review its findings as
  // text? On by default; off, a blind reviewer reads the JSX instead.
  const [piRenderDescribe, setPiRenderDescribe] = useState(true);
  const [piSubagents, setPiSubagents] = useState(false);
  // The model that review runs on, when it should not be the build one — same
  // endpoint, same key, just a second model id. Blank follows the build model,
  // which reviews from source when it cannot take images.
  const [piReviewModel, setPiReviewModel] = useState('');
  // Which engine new decks build on by default. The wizard can override it for
  // one build; this is the value its row starts on.
  const [buildEngine, setBuildEngine] = useState('claude');
  // '' = don't send reasoning_effort at all (the gateway's own per-model
  // default). It spans a catalogue of models with different curves, so there
  // is no rung we can pick on their behalf.
  const [gatewayEffort, setGatewayEffort] = useState('');
  // The OpenCode BUILD's own model and rung, set in Deck builds rather than
  // Chat. '' on either = follow the chat slot / send no rung at all, which is
  // what an OpenCode build did before these existed.
  const [fastBuildModel, setFastBuildModel] = useState('');
  const [briefImageModel, setBriefImageModel] = useState('');
  const [fastBuildEffort, setFastBuildEffort] = useState('');
  const [claudeChatEffort, setClaudeChatEffort] = useState(''); // '' = default (medium)
  const [gatewayModel, setGatewayModel] = useState('');
  // The gateway's ADDRESS. Not a secret, so it saves with everything else — but
  // it lives beside its key, because a key without an address configures
  // nothing and the two were only ever separable while both were env-only.
  const [gatewayBaseUrl, setGatewayBaseUrl] = useState('');
  // What the gateway's own /models endpoint says it serves — a real picker
  // when it answers, free text when it doesn't. null = not fetched yet.
  const [gatewayModels, setGatewayModels] = useState<GatewayModelsPayload | null>(null);
  // Pi: the one engine whose PROVIDER is a setting rather than a fact about the
  // engine, so it carries an address and a wire format alongside its model and
  // rung. Every one of them is Pi's own — pointing this engine at a new
  // provider must never move the OpenCode gateway's chat and build slots, which is
  // the mistake that cost a day of trialling (2026-08-07).
  // WHOSE CREDENTIAL PI SPENDS: 'api' (its own metered key, this engine's
  // default and all it could do before 2026-08-10) or 'subscription' (a
  // `pi /login` on the server). Unlike Claude and Codex it defaults to the
  // METERED branch — Pi does not run at all until a deploy turns it on and a key
  // is pasted, so the metered choice was already made explicitly.
  const [piAuth, setPiAuth] = useState('api');
  // …and WHICH login, as Pi's own CLI names the provider. Free text: Pi ships
  // ChatGPT, Claude Pro/Max, Copilot, xAI, OpenRouter and Radius today and adds
  // to that on its own release cycle, so a select here would go stale.
  const [piSubscriptionProvider, setPiSubscriptionProvider] = useState('');
  const [piProfiles, setPiProfiles] = useState<PiProfile[]>([]);
  // The wire-format probe. Its own state rather than a field on the settings
  // payload: it reports on the values ON SCREEN, which are exactly the ones not
  // saved yet.
  const [apiProbe, setApiProbe] = useState<{ busy: boolean; detail: string; issue: string }>({
    busy: false,
    detail: '',
    issue: '',
  });
  // What the signed-in provider serves, or null on the metered branch and
  // whenever it cannot be read. Null is the FREE-TEXT case, which is what every
  // Pi model field was before a login existed — never an error state.
  const [piCatalogue, setPiCatalogue] = useState<PiCatalogueModel[] | null>(null);
  // The "Refresh models" button's own state. `count` is null until a refresh has
  // landed in THIS drawer session, so the line below the button says "just now"
  // only when it is true — a count carried over from the page load would be a
  // claim nobody made.
  const [piRefresh, setPiRefresh] = useState<{ busy: boolean; issue: string | null; count: number | null }>({
    busy: false,
    issue: null,
    count: null,
  });
  const [piBaseUrl, setPiBaseUrl] = useState('');
  const [piApi, setPiApi] = useState('openai-completions');
  const [piModel, setPiModel] = useState('');
  const [piBuildEffort, setPiBuildEffort] = useState('');
  // Chat's own Pi slots. Blank means "the same Pi as builds", which is what a
  // one-model server wants — never a second endpoint or a second key.
  const [piChatModel, setPiChatModel] = useState('');
  const [piChatEffort, setPiChatEffort] = useState('');
  // Declared to Pi, which reserves against it when it decides to compact — so
  // it is a number the endpoint has to be able to carry, not a preference.
  const [piContextWindow, setPiContextWindow] = useState('');
  // WHOSE CREDIT CODEX SPENDS: 'subscription' (the ChatGPT plan signed in on
  // this server, free at the point of use) or 'api' (a metered OpenAI key).
  // The only row in this drawer that decides whether work costs money, which is
  // why its hint says the price rather than describing the control.
  // The same question for Claude, and the reason each engine's card leads with
  // its account: 'subscription' (the Claude Code login on this server, free at
  // the point of use) or 'api' (a metered Anthropic key). Applies to builds AND
  // chat — one engine spends one credential.
  const [claudeAuth, setClaudeAuth] = useState('subscription');
  // Which engine the chat model pill STARTS on. A preference, not a lock — the
  // pill still switches per message, like the wizard's build-engine row.
  const [chatEngine, setChatEngine] = useState('claude');
  const [resolvedModels, setResolvedModels] = useState<ResolvedModels | null>(null);
  // How many builds are running RIGHT NOW. Read here, not just inside the
  // (collapsible) panel: cancelling a stuck build is the reason that section
  // exists, and a section you have to open to discover is no use for it.
  // From the shared poller (lib/useBuilds.js) — this used to be a second
  // interval over the same endpoint the panel below already polled.
  const { running: runningBuilds, builds: buildList } = useBuilds();
  const [checkingModels, setCheckingModels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // The form body as it was when the drawer last agreed with the server. null
  // until the first payload has been read into the fields, which is what keeps
  // a half-seeded drawer from announcing changes nobody made.
  const [savedBody, setSavedBody] = useState<string | null>(null);
  // Bumped by the load and by a successful save, and by nothing else. It is
  // what `savedBody` follows — see the effect below for why that is not `data`.
  const [syncToken, setSyncToken] = useState(0);

  useEffect(() => {
    api.get<SettingsPayload>('/api/settings').then(
      (d) => {
        setData(d);
        // Hoisted: `settings` is optional on the payload (a server that failed
        // to read its store still answers), and every line below defends with
        // `|| …` anyway.
        const s = d.settings || {};
        // `!== false` on all three: absence means offered, never hidden.
        setClaudeEnabled(s.claudeEnabled !== false);
        setFastEnabled(s.fastEnabled !== false);
        setPiEnabled(s.piEnabled !== false);
        setClaudeModel(s.claudeModel || '');
        setClaudeBuildModel(s.claudeBuildModel || '');
        setOfferSonnet(s.offerSonnet === true);
        setFillFloor(typeof s.fillFloor === 'number' ? s.fillFloor : 0.4);
        setPiReviewPass(s.piReviewPass !== false);
        setPiSvgArt(s.piSvgArt === true);
        setPiRenderDescribe(s.piRenderDescribe !== false);
        setPiSubagents(s.piSubagents === true);
        setPiReviewModel(s.piReviewModel || '');
        setBuildEngine(d.build?.engine || 'claude');
        setGatewayEffort(s.gatewayEffort || '');
        setFastBuildModel(s.fastBuildModel || '');
        setBriefImageModel(s.briefImageModel || '');
        setFastBuildEffort(s.fastBuildEffort || '');
        setClaudeChatEffort(s.claudeChatEffort || '');
        setGatewayModel(s.model || '');
        setGatewayBaseUrl(s.gatewayBaseUrl || '');
        // The effective endpoint (setting → env), so the field shows what the
        // next build would talk to rather than only what is stored here.
        setPiBaseUrl(s.piBaseUrl || d.build?.engines?.pi?.baseUrl || '');
        setPiApi(s.piApi || 'openai-completions');
        setPiModel(s.piModel || '');
        setPiBuildEffort(s.piBuildEffort || '');
        setPiChatModel(s.piChatModel || '');
        setPiChatEffort(s.piChatEffort || '');
        setPiContextWindow(s.piContextWindow ? String(s.piContextWindow) : '');
        // From the ENGINE BLOCK rather than the stored settings, like Claude
        // below: both fields resolve through env on the server, so the
        // drawer must show what the next build would actually use.
        setPiAuth(d.build?.engines?.pi?.auth || 'api');
        setPiSubscriptionProvider(s.piSubscriptionProvider || d.build?.engines?.pi?.subscriptionProvider || '');
        setPiProfiles(Array.isArray(s.piProfiles) ? s.piProfiles : []);
        setClaudeAuth(d.build?.engines?.claude?.auth || 'subscription');
        setChatEngine(d.chat?.engine || 'claude');
        setSyncToken((t) => t + 1);
      },
      (err) => setError(errorMessage(err)),
    );
  }, []);

  /**
   * The signed-in provider's model catalogue, re-read whenever the branch or the
   * provider changes.
   *
   * ONLY on the subscription branch: a metered Pi is pointed at an arbitrary
   * endpoint whose catalogue we have no way to ask for, which is exactly why
   * these fields are free text in the first place. A failure here is silent by
   * design — the fields fall back to what they always were.
   */
  useEffect(() => {
    // A refresh result belongs to ONE provider. Changing the branch or the
    // provider makes "Updated just now · N models" a statement about a list that
    // is no longer on screen, so it goes with the list.
    setPiRefresh({ busy: false, issue: null, count: null });
    if (piAuth !== 'subscription') {
      setPiCatalogue(null);
      return;
    }
    let alive = true;
    const q = piSubscriptionProvider.trim() ? `?provider=${encodeURIComponent(piSubscriptionProvider.trim())}` : '';
    api
      .get<{ models?: PiCatalogueModel[] }>(`/api/settings/pi/models${q}`)
      .then((d) => alive && setPiCatalogue(d.models?.length ? d.models : null))
      .catch(() => alive && setPiCatalogue(null));
    return () => {
      alive = false;
    };
  }, [piAuth, piSubscriptionProvider]);

  // Re-baseline on the two moments the drawer and the server actually agree:
  // the first load, and a successful save. Both bump `syncToken`.
  //
  // Keyed off that token and NOT off `data`, which looks like the same thing
  // and is not: `mergeServerState` also writes `data`, on a credential write,
  // and is documented to leave unsaved edits standing. Re-baselining there
  // adopted whatever was half-typed as the saved truth, so the flag cleared
  // itself on a change nobody had saved — the exact lie this is here to stop.
  //
  // An effect rather than an inline read because the token is bumped in the
  // same batch as the field setters; by the time this runs they have committed,
  // so `formBody()` sees the seeded fields. And it reads the FIELDS rather than
  // the payload on purpose: `formBody` is the only thing that knows how a field
  // becomes a wire value, and a second transcription of those rules here would
  // drift and start flagging rows nobody touched.
  useEffect(() => {
    if (syncToken > 0) setSavedBody(JSON.stringify(formBody()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncToken]);

  // Ask the gateway what models it serves, once, when a gateway key exists.
  // Server-cached ~10min; a gateway without /models degrades to free text.
  const gatewayAvailable = Boolean(data?.available?.gateway);
  useEffect(() => {
    if (!gatewayAvailable || gatewayModels !== null) return;
    api.get<GatewayModelsPayload>('/api/settings/gateway-models').then(
      (d) => setGatewayModels(d),
      () => setGatewayModels({ models: [] }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayAvailable]);

  // In API mode the roster is the CHATGPT account's, so it describes a different
  // service. The cache flags which models are reachable over the API; mark those
  // that are not, rather than hiding them — a key on another org may well reach
  // a model this cache never mentioned, and the service is the judge. An absent
  // flag (older cache) says nothing and is left alone.

  // Changing the model can strip the rung the old one offered. Drop back to

  // THE SAME RULE ON THE GATEWAY SIDE, and for the same reason it was needed on
  // This drawer used to hard-code one seven-rung ladder for every
  // OpenCode model, because that is the list the gateway's validator names — but
  // that list is the UNION across its whole roster, not one model's dial. On
  // deepseek-v4-flash, whose real rungs are high|max, picking `medium` here was
  // dropped by `usableGatewayEffort` before the call, so the setting saved,
  // displayed, and did nothing. Three states, kept apart (see
  // server/lib/gateway-models.ts):
  //   rungs listed → offer those, plus None, which is an off switch every model
  //                  with a dial honours and models.dev does not list
  //   []           → the model reasons with NO settable rung: show no dial at all
  //   absent       → an id our roster has never heard of (or a roster we could
  //                  not read): offer the union and let the gateway judge
  const gatewayRungs = (want: string) => gatewayRungsFor(gatewayModels?.efforts, want);
  // Blank on either field follows the other surface, so the dial's model is the
  // RESOLVED one, not the field's text.
  const fastBuildRungs = gatewayRungs(fastBuildModel || data?.fastChat?.model || '');

  // CAN THIS MODEL READ AN IMAGE. Same three states as the rungs above and the
  // same reason to keep them apart: `false` is "this model is text-only, say
  // so", `undefined` is "an id we do not recognise, stay quiet". Guessing on an
  // unknown id would put a warning on every model newer than our snapshot.
  //
  // It matters because a brief's content can BE a picture — a process diagram
  // pasted into a Word doc — and a text-only build model receives none of it,
  // silently. The build succeeds; the section is simply missing from the deck.
  const readsImages = (want: string | undefined | null): boolean | undefined => {
    const map = gatewayModels?.vision;
    if (!want || !map || !Object.prototype.hasOwnProperty.call(map, want)) return undefined;
    return map[want];
  };
  const fastBuildSeesImages = readsImages(fastBuildModel || data?.fastChat?.model || '');
  // The Pi reviewer's eyes, on the same three states. It is the one field where
  // the answer is the point of the field: a review model that cannot take an
  // image reviews from the source, which is what the blank slot already does.
  // Silent on an unknown id — this engine's provider is a setting, so most ids
  // will not be on a roster read off one gateway.
  //
  // THE LOGIN'S OWN CATALOGUE WINS when there is one. `readsImages` reads the
  // gateway roster, which is a fact about models the OPENCODE engine's endpoint
  // serves; a signed-in Pi provider states its own modalities, and that is the
  // answer for the model actually about to run. Falls back to the roster, then
  // to silence.
  const piCatalogueVision = (want: string | undefined | null): boolean | undefined => {
    const hit = want ? piCatalogue?.find((m) => m.id === want) : null;
    return hit && hit.vision !== null ? hit.vision : undefined;
  };
  const piReviewSeesImages = piCatalogueVision(piReviewModel) ?? readsImages(piReviewModel);
  const visionMap = gatewayModels?.vision;
  const visionModelIds = visionMap ? Object.keys(visionMap).filter((id) => visionMap[id]) : [];
  const gatewayChatRungs = gatewayRungs(gatewayModel || data?.fastChat?.model || '');

  const fastBuildEffortKey = fastBuildRungs.efforts.join(',');
  const gatewayEffortKey = gatewayChatRungs.efforts.join(',');

  // Switching models can strip the rung the old one offered — fall back to the
  // model's own default rather than storing one that would be silently dropped.
  // Only a KNOWN roster acts: an unrecognised id is not evidence against a rung.
  useEffect(() => {
    if (!fastBuildRungs.known) return;
    setFastBuildEffort((e) => (e && !fastBuildRungs.efforts.includes(e) ? '' : e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fastBuildEffortKey, fastBuildRungs.known]);
  useEffect(() => {
    if (!gatewayChatRungs.known) return;
    setGatewayEffort((e) => (e && !gatewayChatRungs.efforts.includes(e) ? '' : e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayEffortKey, gatewayChatRungs.known]);

  /**
   * Ask the server which wire format this endpoint actually speaks, and set the
   * dropdown to the answer.
   *
   * The failure it exists for is not a bad guess but a DEAD BUILD: measured
   * 2026-08-10, `gpt-5.6-luna` returns "Stream ended without finish_reason" over
   * chat completions and answers over Responses, while `deepseek-v4-flash` is
   * happy on completions — the default. Two builds died on that dropdown before
   * anyone suspected it.
   *
   * Never throws and never blocks a save: a probe that cannot run leaves the
   * field exactly as the author set it and says why.
   */
  async function probeApiStyle() {
    setApiProbe({ busy: true, detail: '', issue: '' });
    try {
      const r = await api.post<{
        api?: string | null;
        issue?: string;
        tried?: { api: string; ok: boolean; detail: string }[];
      }>('/api/settings/pi/probe-api', { baseUrl: piBaseUrl.trim(), model: piModel.trim() });
      if (r.api === 'openai-responses' || r.api === 'openai-completions') {
        setPiApi(r.api);
        const name = r.api === 'openai-responses' ? 'Responses' : 'chat completions';
        setApiProbe({
          busy: false,
          detail: `${piModel.trim()} answered over ${name}, so that is what Pi will send.`,
          issue: '',
        });
        return;
      }
      // Neither worked. Say what each one did — the reason is usually the
      // model id or the key, not the format, and the detail is what shows that.
      const why = (r.tried || []).map((t) => `${t.api}: ${t.detail}`).join(' · ');
      setApiProbe({
        busy: false,
        detail: '',
        issue: r.issue || `Neither format answered. ${why || 'No detail from the endpoint.'}`,
      });
    } catch (e) {
      setApiProbe({ busy: false, detail: '', issue: errorMessage(e) });
    }
  }

  /**
   * Ask the provider's own catalogue service for models newer than the roster
   * baked into the installed pi CLI, and put the answer straight into the field.
   *
   * On a button rather than on load, like the API-style probe above and for the
   * same reason: it is a live network call, and a drawer that blocked on one
   * every time the provider changed would read as hung. The server persists the
   * result, so the next page load gets it for free.
   */
  async function refreshPiModels() {
    setPiRefresh({ busy: true, issue: null, count: null });
    try {
      const r = await api.post<PiModelRefresh>('/api/settings/pi/models/refresh', {
        provider: piSubscriptionProvider.trim(),
      });
      // An empty list never replaces a good one: the issue text explains what
      // happened, and the options already on screen stay usable.
      if (r.models?.length) setPiCatalogue(r.models);
      setPiRefresh({ busy: false, issue: r.issue || null, count: r.models?.length ?? 0 });
    } catch (e) {
      setPiRefresh({ busy: false, issue: errorMessage(e), count: null });
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const d = await api.put<SettingsPayload>('/api/settings', formBody());
      setData(d);
      setSaved(true);
      setSyncToken((t) => t + 1);
      // Tell the command bar's model pill the OpenCode model may have changed.
      window.dispatchEvent(new CustomEvent('studio:settings-changed', { detail: d }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Everything Save would PUT, from the fields exactly as they stand.
   *
   * Its own function because two things need it and they must never disagree:
   * the request, and the check for whether there is anything to send. A dirty
   * flag per row would drift the first time a row was added and quietly start
   * lying — and the whole point of the flag is to be believed.
   */
  /** Is this engine still on offer, according to the switches as they stand in
   *  the form? Read by the two "which engine does X start on" fields, which
   *  must never name an engine this same save is turning off. */
  function engineIsOffered(engine: string): boolean {
    if (engine === 'claude') return claudeEnabled;
    if (engine === 'fast') return fastEnabled;
    if (engine === 'pi') return piEnabled;
    return true;
  }

  function formBody(): Record<string, unknown> {
    return {
      // Offered IS the default on all three, so only the OFF answer is stored.
      claudeEnabled: claudeEnabled ? null : false,
      fastEnabled: fastEnabled ? null : false,
      piEnabled: piEnabled ? null : false,
      offerSonnet: offerSonnet ? true : null,
      // Retiring Sonnet retires a saved Sonnet default with it — the server
      // coerces it anyway, so leaving it stored would only be a trap for
      // whoever reads settings.json next.
      claudeModel: (offerSonnet ? claudeModel.trim() : dropSonnet(claudeModel)) || null,
      claudeBuildModel: claudeBuildModel || null,
      fillFloor: fillFloor,
      // ON is the default, so it stores nothing — only the OFF answer is worth
      // a line in settings.json.
      piReviewPass: piReviewPass ? null : false,
      // OFF is the default here, so only the ON answer is stored.
      piSvgArt: piSvgArt ? true : null,
      piRenderDescribe: piRenderDescribe ? null : false,
      // OFF is the default (2026-09-08), so only the ON answer is stored.
      piSubagents: piSubagents ? true : null,
      piReviewModel: piReviewModel.trim() || null,
      // An engine you have just switched off cannot also be the engine new
      // decks build on. The server refuses that pairing (rightly — a stored
      // default that always 409s is worse than none), and it would refuse the
      // WHOLE save, taking the switch you came here to flip with it. So the
      // default comes home to Claude in the same body. Same for the chat pill
      // below, which would otherwise start on an engine it cannot offer.
      buildEngine: buildEngine === 'claude' || !engineIsOffered(buildEngine) ? null : buildEngine,
      gatewayEffort: gatewayEffort || null,
      fastBuildModel: fastBuildModel.trim() || null,
      briefImageModel: briefImageModel.trim() || null,
      fastBuildEffort: fastBuildEffort || null,
      claudeChatEffort: claudeChatEffort || null,
      // 'subscription' IS the default, so it stores nothing — an absent field
      // and an explicit choice of the free path must not drift apart.
      claudeAuth: claudeAuth === 'api' ? 'api' : null,
      // BOTH answers are written, unlike the two above: this engine's dial
      // falls back to env PI_AUTH, so "the default stores nothing" would let a
      // deploy override the choice someone just made here (see getPiAuth).
      piAuth: piAuth === 'subscription' ? 'subscription' : 'api',
      piSubscriptionProvider: piSubscriptionProvider.trim() || null,
      piBaseUrl: piBaseUrl.trim() || null,
      // chat/completions IS the default, so it stores nothing: an absent field
      // and an explicit choice of the common case must not drift apart.
      piApi: piApi === 'openai-responses' ? 'openai-responses' : null,
      piModel: piModel.trim() || null,
      piProfiles: piProfiles.length > 0 ? piProfiles : null,
      piBuildEffort: piBuildEffort || null,
      piChatModel: piChatModel.trim() || null,
      piChatEffort: piChatEffort || null,
      piContextWindow: Number(piContextWindow) > 0 ? Number(piContextWindow) : null,
      chatEngine: chatEngine === 'claude' || !engineIsOffered(chatEngine) ? null : chatEngine,
      gatewayBaseUrl: gatewayBaseUrl.trim() || null,
      // Write the gateway model whenever there is a gateway key, which is
      // exactly when the field above is rendered and seeded. Writing it back
      // is a no-op rather than a wipe: the field is always seeded from
      // `s.model` when a key exists.
      ...(gatewayAvailable ? { model: gatewayModel.trim() || null } : {}),
    };
  }

  // Ask the server what the installed Claude CLI resolves the sonnet/opus
  // aliases to — the one-click "is this box actually on Sonnet 5?" check.
  // Spawns a throwaway `claude -p` per alias server-side (cached ~10min).
  async function checkResolvedModels() {
    setCheckingModels(true);
    try {
      setResolvedModels(await api.get<ClaudeResolvedModels>('/api/settings/claude-resolved-model'));
    } catch (err) {
      setResolvedModels({ error: errorMessage(err) });
    } finally {
      setCheckingModels(false);
    }
  }

  // ONE preset since 2026-08-10, so there is no provider picker left: the
  // Luna-locked `openai` one went with it (Pi is how a second metered vendor is
  // reached now, and api.openai.com is just another gateway address). What is
  // left is a key, an address and a model.
  const av = data?.available || {};
  // Whether the gateway has a key at all. Used to decide whether OpenCode is
  // worth naming in the Engines summary.
  const fastConfigured = Boolean(av.gateway);

  // Defaults first on a first visit: it is the section with a decision in it.
  // The rest are one engine's setup, or status, and the rail says enough about
  // them without being opened.
  const [section, goSection] = useSection('defaults');

  /** Send someone from a row to the section that owns its answer. The pane
   *  swaps and the rail moves with it, so the link lands somewhere that says
   *  where it landed. */
  function revealGroup(id: string) {
    goSection(id);
    requestAnimationFrame(() => {
      document.getElementById(`settings-pane-${id}`)?.scrollIntoView({ block: 'start' });
    });
  }

  // The engines this server can build on besides Claude. Each is offered only
  // where it's configured — a choice with one answer is not a question — and an
  // engine that's configured but can't start right now stays visible and
  // disabled, carrying the server's own reason.
  const buildEngines = data?.build?.engines || {};
  // Masked credential status per provider — present, a last-four hint, and
  // whether the value came from this drawer or the server env. Never a value.
  const creds = data?.credentials || {};

  // A credential write returns the status blocks it can change (an engine may
  // have just become available, and the OpenCode provider may auto-detect
  // differently), so merge rather than replace: `settings` is not in that
  // response and the drawer's unsaved edits must survive it.
  function mergeServerState(res: Partial<SettingsPayload>) {
    setData((prev) => ({ ...(prev || {}), ...res }));
    // The command bar's model pill reads the same availability.
    window.dispatchEvent(new CustomEvent('studio:settings-changed', { detail: res }));
  }
  /**
   * Re-read the server's STATUS blocks after something outside this form
   * changed them — today, a Pi sign-in or sign-out, which flips the engine
   * between available and "no login for X".
   *
   * The same merge rule as `mergeServerState`, and for the same reason: the
   * drawer's unsaved edits are the user's, so `settings` from the response is
   * deliberately dropped and only the derived blocks are taken.
   */
  function refreshServerState() {
    api
      .get<SettingsPayload>('/api/settings')
      .then(({ settings: _ignored, ...blocks }) => mergeServerState(blocks))
      .catch(() => {
        /* the drawer is still usable; the next open re-reads anyway */
      });
  }

  // What an OpenCode BUILD would run on: the unsaved build slot, else whatever
  // the server resolved (build slot → chat slot → env). Never just the chat
  // model — that was the bug this section fixes.
  const fastModelName = fastBuildModel || buildEngines.fast?.model || data?.fastChat?.model || null;
  const fastEngine = buildEngines.fast || null;
  // Both gated on the BLOCK existing, never on `.model`. OpenCode used to be
  // gated on its model, which hid the option on exactly the servers that needed
  // it most: `fastBuildIssue()` already returns "No OpenCode model chosen — pick
  // one in the ⚙ drawer.", and hiding the row hid the only place that sentence
  // could appear. An engine that cannot run states why.
  const extraEngines = [
    buildEngines.fast && { value: 'fast', label: 'OpenCode', ...buildEngines.fast },
    buildEngines.pi && { value: 'pi', label: 'Pi', ...buildEngines.pi },
  ].filter((e): e is EngineRow => Boolean(e));
  const blockedEngines = extraEngines.filter((e) => !e.available && e.issue);

  /** The one sentence above an engine's BUILD rows: what a build on it runs on
   *  and what it costs, or — when it can't run — what would fix it. Written per
   *  engine because the Claude CLI's whereabouts is no answer at all to someone
   *  building on Pi. */
  function buildIntro(engine: string): ReactNode {
    if (engine === 'fast') {
      const fast = buildEngines.fast;
      if (fast && !fast.available) return fast.issue;
      return fastModelName ? (
        <>
          OpenCode builds run on the metered gateway model <strong>{fastModelName}</strong>.
        </>
      ) : (
        'OpenCode builds run on the metered gateway model. Pick one below.'
      );
    }
    if (engine === 'pi') {
      const pi = buildEngines.pi;
      if (pi && !pi.available) return pi.issue || 'Pi is not usable on this server.';
      // Names the ENDPOINT, not a vendor: this engine exists so the provider can
      // change without code, so a sentence naming one would be wrong the first
      // time anybody used the feature.
      return pi?.model && pi?.baseUrl ? (
        <>
          Pi builds run on <strong>{pi.model}</strong> at <strong>{pi.baseUrl}</strong>, metered against the Pi API key.
        </>
      ) : (
        'Pi builds run on whichever OpenAI-compatible endpoint you point it at, and they are always metered. Set its address, key and model below.'
      );
    }
    const claude = buildEngines.claude;
    if (claude && !claude.available) return claude.issue || 'Claude is not usable on this server.';
    return claudeAuth === 'api'
      ? 'Claude builds are charged to the Anthropic API key, at API rates, rather than to the subscription.'
      : 'Claude builds run on the subscription signed in on this server. No cost per build.';
  }

  /** An engine as the user knows it, and the group that now owns it. One card
   *  per engine since 2026-08-10, so an id and a group id are the same thing. */
  const engineLabel = (id: string) => (id === 'fast' ? 'OpenCode' : id === 'pi' ? 'Pi' : 'Claude');
  const engineGroup = (id: string) => (id === 'fast' ? 'opencode' : id === 'pi' ? 'pi' : 'claude');

  // The engines a MESSAGE can run on. Same shape as the build row, from the
  // server's own `chat` block, so the two rows cannot disagree about what this
  // server can do. Claude is always listed: it is the default and, when it is
  // missing, its absence is the thing worth saying.
  const chatBlocks = data?.chat?.engines || {};
  const chatEngines = [
    { value: 'claude', label: 'Claude', ...(chatBlocks.claude || { available: false }) },
    chatBlocks.fast && { value: 'fast', label: 'OpenCode', ...chatBlocks.fast },
    chatBlocks.pi && { value: 'pi', label: 'Pi', ...chatBlocks.pi },
  ].filter((e): e is EngineRow => Boolean(e));
  // PI'S DIAL IS THE MODEL'S TOO (2026-08-07, Jake: "the thinking effort needs
  // to come from the model — all models have differing available rungs").
  // Pi takes seven rungs on `--thinking` and maps whichever it gets onto the
  // endpoint's own reasoning parameter, so the seven are a CEILING: on a model
  // we know, the rungs it does not have are coerced or refused. The table is
  // the server's `modelRungs` — the same knowledge `piEffortLadder()` narrows
  // with — and `off` survives on every model because it is Pi's own rung
  // (send no `--thinking`) rather than the provider's. An id the table has
  // never heard of is the NORMAL case on this engine, and it keeps the full
  // ladder: our ignorance must not block a provider that shipped yesterday.
  const piBuildRungs = piRungs(data?.modelRungs, piModel || buildEngines.pi?.model || '');
  const piChatRungs = piRungs(data?.modelRungs, piChatModel || piModel || chatBlocks.pi?.model || '');
  const piBuildEffortKey = piBuildRungs.efforts.join(',');
  const piChatEffortKey = piChatRungs.efforts.join(',');

  // Typing a different model can strip the rung the old one offered — fall back
  // to "the model's own default" rather than storing one the runner would then
  // drop in silence. Only a KNOWN model acts, for the same reason as above.
  useEffect(() => {
    if (!piBuildRungs.known) return;
    setPiBuildEffort((e) => (e && !piBuildRungs.efforts.includes(e) ? '' : e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piBuildEffortKey, piBuildRungs.known]);
  useEffect(() => {
    if (!piChatRungs.known) return;
    setPiChatEffort((e) => (e && !piChatRungs.efforts.includes(e) ? '' : e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piChatEffortKey, piChatRungs.known]);

  /** The same sentence for an engine's CHAT rows — what a message on it runs on
   *  and what it costs, or what would fix it. Twin of `buildIntro`: the two jobs
   *  are separate settings on every engine, so each says its own price. */
  function chatIntro(engine: string): ReactNode {
    const pick = chatEngines.find((e) => e.value === engine);
    if (pick && !pick.available) return pick.issue || `${pick.label} cannot answer messages here.`;
    if (engine === 'fast') {
      return (
        <>
          Messages run on the metered model <strong>{data?.fastChat?.model || data?.fastChat?.provider}</strong>. Every
          message can still be sent to another engine from the chat&apos;s model pill.
        </>
      );
    }
    if (engine === 'pi') {
      // The MODEL and the ENDPOINT, never a vendor name: this engine can be
      // pointed anywhere, so the row has to say what a message would talk to.
      return (
        <>
          Messages run on <strong>{piChatModel || chatBlocks.pi?.model || piModel || 'no model'}</strong>{' '}
          {piAuth === 'subscription' ? (
            <>
              on the <strong>{piSubscriptionProvider || 'openai-codex'}</strong> login signed in on this server, so
              nothing is charged to the Pi key
            </>
          ) : (
            <>
              at <strong>{chatBlocks.pi?.baseUrl || piBaseUrl || 'no endpoint'}</strong>, metered against the Pi API key
            </>
          )}
          . Every message can still be sent to another engine from the chat&apos;s model pill.
        </>
      );
    }
    return (
      <>
        Messages run on <strong>Claude</strong> (
        {claudeAuth === 'api' ? 'charged to the Anthropic API key' : 'the server’s subscription'}), default{' '}
        {data?.claude?.model === 'sonnet' ? 'Sonnet' : 'Opus'}
        {data?.claude?.offerSonnet ? '' : ' on every turn (Sonnet is switched off below)'}.
        {chatEngines.length > 1 ? " Every message can still be sent to another engine from the chat's model pill." : ''}
      </>
    );
  }

  // Each group's head line — its CURRENT ANSWER, in the words the controls use.
  // ONE RULE FOR EVERY HEAD: money is marked, free is not. Marking the exception
  // rather than labelling both states keeps the summary scannable, which is the
  // whole reason a closed group is safe.
  //
  // An engine that cannot run has no account worth naming — saying "Claude
  // subscription" over a Claude that is switched off is the closed drawer
  // lying, which defeats the whole point of the summary.
  const accountNow = (id: string, mode: string) =>
    buildEngines[id] && !buildEngines[id]?.available ? 'off' : mode === 'api' ? 'API key' : 'subscription';
  const defaultsNow = `builds ${engineLabel(buildEngine)} · chat ${engineLabel(chatEngine)}`;
  // One head per engine since 2026-08-10, so each says its own account and both
  // of its jobs — the two questions the card answers.
  const claudeNow = `${accountNow('claude', claudeAuth)} · builds ${
    claudeBuildModel === 'sonnet' ? 'Sonnet' : 'Opus'
  } · chat ${claudeModel === 'sonnet' ? 'Sonnet' : 'Opus'} ${claudeChatEffort || 'medium'}`;
  const opencodeNow = !fastConfigured
    ? 'no key'
    : `${buildEngines.fast && !buildEngines.fast.available ? 'off' : 'metered'} · builds ${
        fastModelName || 'no model'
      }${fastBuildEffort ? ` · ${fastBuildEffort}` : ''} · chat ${gatewayModel || data?.fastChat?.model || 'no model'}${
        gatewayEffort ? ` · ${gatewayEffort}` : ''
      }`;
  const piNow = !buildEngines.pi
    ? 'not configured'
    : `${
        !buildEngines.pi.available
          ? 'off'
          : piAuth === 'subscription'
            ? piSubscriptionProvider || 'subscription'
            : 'metered'
      } · builds ${piModel || buildEngines.pi.model || 'no model'}${
        piBuildEffort ? ` · ${piBuildEffort}` : ''
      } · chat ${piChatModel || chatBlocks.pi?.model || piModel || 'no model'}${
        piChatEffort ? ` · ${piChatEffort}` : ''
      }`;
  // "images" names the FALLBACK reader, since the engine's own model is what
  // normally does the looking and the engine cards already say which that is.
  const qualityNow = `${fillFloor === 0 ? 'fill off' : `fill ${Math.round(fillFloor * 100)}%`} · fallback images ${
    briefImageModel || 'mimo-v2.5'
  }`;

  // THE RAIL SAYS LESS THAN THE PANE, on purpose. An engine's full answer names
  // its account and both of its models, which is four facts and about sixty
  // characters — in a 15rem rail that is an ellipsis two thirds of the way
  // through, so the line stops distinguishing the engines it exists to
  // distinguish. What survives the cut is whose credential it spends and what a
  // BUILD runs on; the pane header, which has the width for it, carries the
  // rest.
  const claudeRail = `${accountNow('claude', claudeAuth)} · ${claudeBuildModel === 'sonnet' ? 'Sonnet' : 'Opus'}`;
  const opencodeRail = !fastConfigured
    ? 'no key'
    : `${buildEngines.fast && !buildEngines.fast.available ? 'off' : 'metered'} · ${fastModelName || 'no model'}`;
  const piRail = !buildEngines.pi
    ? 'not configured'
    : `${
        !buildEngines.pi.available
          ? 'off'
          : piAuth === 'subscription'
            ? piSubscriptionProvider || 'subscription'
            : 'metered'
      } · ${piModel || buildEngines.pi.model || 'no model'}`;

  // WHETHER THERE IS ANYTHING TO SAVE. Nothing in this drawer takes effect
  // until Save is pressed, and nothing said so: a model picked in a select
  // looks identical whether it is stored or still sitting in React state, and
  // the server then answers "No OpenCode model chosen" about a drawer that is
  // visibly showing one. That cost an afternoon (Alex, 2026-08-07).
  //
  // Compared BY VALUE against the body as it stood when the drawer last agreed
  // with the server, so putting a field back where it was clears the flag too —
  // a "you have changes" that will not go away is the version of this people
  // learn to ignore.
  const dirty = savedBody !== null && JSON.stringify(formBody()) !== savedBody;

  // A metered mode with no key left to spend is the one state the drawer can be
  // in that the server will refuse to store. Save is ONE batch PUT, so a single
  // invalid field would reject twenty valid ones — block it here and say which,
  // rather than letting the whole save 400.
  //
  // It clears itself the moment a key is pasted, because a credential write
  // refreshes `data.credentials`. And it is only reachable by removing a key
  // AFTER choosing the mode, which is rare, and when it happens the drawer is
  // exactly where the person is standing.
  const saveBlock =
    claudeAuth === 'api' && !creds.anthropic?.present
      ? 'Claude is set to an API key and there is none on this server. Add the key in the Claude group, or switch Claude back to the subscription.'
      : null;
  const mediaNow = data?.media?.unsplash
    ? data?.media?.pexels
      ? 'Unsplash + Pexels'
      : 'Unsplash, no video'
    : data?.media?.pexels
      ? 'Pexels'
      : 'placeholders only';

  /** An engine's mark in the rail: is this server offering it at all, and if
   *  not, is that a switch or a missing setup? Absent for a section that is
   *  not an engine — see `NavItem`. */
  const engineDot = (id: string): 'on' | 'off' | 'unset' => {
    const block = buildEngines[id] || (id === 'fast' ? chatBlocks.fast : id === 'pi' ? chatBlocks.pi : undefined);
    if (!block) return 'unset';
    return block.available ? 'on' : 'off';
  };

  /**
   * The Save button, rendered TWICE — once in the header beside Close, once in
   * the footer (2026-08-11, Jake: "so it's clear you need to save").
   *
   * One definition, two placements, because the two must never disagree about
   * whether there is anything to save or whether saving is refused. The header
   * copy exists because Close is up there: the drawer's most reachable control
   * was the one that throws the edits away, and the one that keeps them was at
   * the other end of the panel.
   */
  const saveButton = (
    <button
      type="button"
      /* Primary only while it has something to do. A Save that looks identical
         whether or not there are changes is a Save nobody reads — and NOT
         disabled when there is nothing pending, so a wrong answer from `dirty`
         can never lock someone out of saving. The colour is the hint; the
         button is always the way out. */
      className={'btn btn--sm ' + (dirty ? 'btn--primary' : 'btn--ghost')}
      onClick={save}
      disabled={saving || Boolean(saveBlock)}
      title={saveBlock || (dirty ? 'Apply these changes' : 'Nothing to save')}
    >
      {saving ? 'Saving…' : dirty ? 'Save changes' : 'Save'}
    </button>
  );

  return (
    <>
      <Drawer
        title="Settings"
        onClose={onClose}
        xl
        flush
        actions={
          data ? (
            <div className="settings__header-save" data-dirty={dirty ? 'true' : 'false'}>
              {/* Says what is true right now, beside the control that resolves
                  it. Hidden when there is nothing pending rather than swapped
                  for "Saved": a permanent status word next to a button is the
                  kind people stop reading. */}
              {dirty && !saveBlock && <span className="settings__unsaved small">Unsaved changes</span>}
              {saveButton}
            </div>
          ) : null
        }
      >
        {!data && !error && <p className="muted small settings-console__loading">Loading…</p>}
        {data && (
          <div className="settings-console">
            {/* THE RAIL IS THE SUMMARY. Every section's current answer is on it,
                so the question "how is this studio set up" is answered by
                reading down the left edge rather than by opening eleven
                sections in turn. */}
            <nav className="settings-console__nav" aria-label="Settings sections">
              <NavItem id="defaults" name="Defaults" now={defaultsNow} active={section} onGo={goSection} />
              <p className="settings-nav__group">Engines</p>
              <NavItem
                id="claude"
                name="Claude"
                now={claudeRail}
                dot={engineDot('claude')}
                active={section}
                onGo={goSection}
              />
              <NavItem
                id="opencode"
                name="OpenCode"
                now={opencodeRail}
                dot={fastConfigured ? engineDot('fast') : 'unset'}
                active={section}
                onGo={goSection}
              />
              <NavItem id="pi" name="Pi" now={piRail} dot={engineDot('pi')} active={section} onGo={goSection} />
              <p className="settings-nav__group">Everything else</p>
              <NavItem id="quality" name="Build quality" now={qualityNow} active={section} onGo={goSection} />
              <NavItem id="media" name="Stock media" now={mediaNow} active={section} onGo={goSection} />
              <NavItem id="storage" name="Storage" now="cached files" active={section} onGo={goSection} />
              <NavItem id="people" name="People" now="who can sign in" active={section} onGo={goSection} />
              <NavItem id="usage" name="Usage costs" now="what each deck spent" active={section} onGo={goSection} />
              <NavItem
                id="running"
                name="Running builds"
                now={buildList === null ? 'checking' : runningBuilds === 0 ? 'none' : `${runningBuilds} running`}
                active={section}
                onGo={goSection}
              />
            </nav>

            <div className="settings-console__main">
              <div className="settings-console__panes">
                {/* DEFAULTS — the only two answers in this drawer that belong to no
                single engine: where a new deck and a new message START.
                Everything else belongs to ONE engine and now lives in that
                engine's own card below. Setting an engine up used to mean
                picking it in three places — the Engines and keys card, then a
                "Settings shown for" tab in Deck builds, then the same tab again
                in Chat — which read as three questions where there was one. */}
                <Pane id="defaults" name="Defaults" now={defaultsNow} active={section}>
                  <p className="small">
                    Where a new deck and a new message start. Each engine&apos;s own account, keys, models and thinking
                    live in its card below.
                  </p>

                  <OptionRows>
                    {/* Only where a second engine exists on this server: a choice
                    with one answer is not a question. */}
                    {extraEngines.length > 0 && (
                      <OptionRow
                        label="Build engine for new decks"
                        hint={
                          <>
                            Which engine the New deck wizard starts on. You can still switch it for a single build.{' '}
                            <button
                              type="button"
                              className="link small"
                              onClick={() => revealGroup(engineGroup(buildEngine))}
                            >
                              {engineLabel(buildEngine)} settings
                            </button>
                          </>
                        }
                        note={blockedEngines.length > 0 ? blockedEngines.map((e) => e.issue).join(' ') : null}
                      >
                        <select
                          value={buildEngine}
                          onChange={(e) => setBuildEngine(e.target.value)}
                          aria-label="Build engine for new decks"
                        >
                          {/* Built from the server block, NOT hardcoded. Claude can
                          be unavailable for reasons of its own — switched off
                          for a client-facing instance, or set to an API key
                          that was removed — and an option that cannot run must
                          say so rather than accepting the click and 409ing the
                          build. */}
                          <option
                            value="claude"
                            disabled={buildEngines.claude ? !buildEngines.claude.available : false}
                          >
                            Claude
                            {buildEngines.claude && !buildEngines.claude.available ? ' (unavailable)' : ''}
                          </option>
                          {extraEngines.map((e) => (
                            <option key={e.value} value={e.value} disabled={!e.available}>
                              {e.label}
                              {!e.available ? ' (unavailable)' : ''}
                            </option>
                          ))}
                        </select>
                      </OptionRow>
                    )}

                    {chatEngines.length > 1 && (
                      <OptionRow
                        label="Chat engine"
                        stack={chatEngines.length > 2}
                        hint={
                          <>
                            Where the chat&apos;s model pill starts. Every message can still be sent to a different
                            engine from the pill itself.{' '}
                            <button
                              type="button"
                              className="link small"
                              onClick={() => revealGroup(engineGroup(chatEngine))}
                            >
                              {engineLabel(chatEngine)} settings
                            </button>
                          </>
                        }
                      >
                        <Segmented
                          value={chatEngine}
                          onChange={setChatEngine}
                          label="Chat engine"
                          wrap={chatEngines.length > 2}
                          options={chatEngines.map((e) => ({
                            value: e.value,
                            label: e.label,
                            disabled: !e.available,
                            title: e.issue || undefined,
                          }))}
                        />
                      </OptionRow>
                    )}
                  </OptionRows>
                </Pane>

                {/* ONE CARD PER ENGINE, and each one answers the same three
                questions in the same order: is it offered and whose credential
                it spends, what a BUILD on it runs on, what a MESSAGE on it runs
                on. Auth first because it outranks both — what a model costs
                stops mattering the moment nobody can reach it. */}
                <Pane id="claude" name="Claude" now={claudeNow} active={section}>
                  <Band name="Account">
                    <EngineAccount
                      name="Claude"
                      status={buildEngines.claude}
                      offered={claudeEnabled}
                      onOffered={setClaudeEnabled}
                      offerCopy={{
                        on: 'Claude builds decks and answers messages. Turn this off to take it off BOTH — the switch for an instance other people use, so their turns cannot reach this server’s subscription.',
                        off: 'Claude is off for everyone on this server: no agent builds, and the chat pill does not offer it. Messages run on the metered engines instead.',
                      }}
                      mode={claudeAuth}
                      onMode={setClaudeAuth}
                      modeCopy={{
                        subscription:
                          'Builds and chat run on the Claude subscription signed in on this server. No cost per build or per message.',
                        api: 'Builds and chat are charged to the Anthropic API key below, at API rates, rather than to the subscription.',
                      }}
                      apiCred={creds.anthropic}
                      creds={[
                        {
                          id: 'anthropic',
                          label: 'Anthropic API key',
                          hint: 'Used only while Claude is set to the API key above.',
                        },
                      ]}
                      credStatuses={creds}
                      onCredChange={mergeServerState}
                    >
                      {/* Which id this server's CLI maps the two aliases to. It lives
                    here rather than in Deck builds because it is a property of
                    the ACCOUNT and the installed CLI, not of a build — and on a
                    metered key it is the answer to "does this key serve Sonnet
                    5". */}
                      <OptionRow
                        label="Resolved model"
                        sub
                        stack
                        hint={
                          resolvedModels
                            ? null
                            : 'What this server’s Claude CLI maps the sonnet and opus aliases to today.'
                        }
                      >
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={checkingModels || !buildEngines.claude?.available}
                          onClick={checkResolvedModels}
                        >
                          {checkingModels ? 'Checking…' : 'Check resolved model'}
                        </button>
                        {resolvedModels && (
                          <p className="small">
                            {resolvedModels.error ? (
                              <span className="muted">{resolvedModels.error}</span>
                            ) : (
                              <>
                                Sonnet →{' '}
                                <code>{resolvedModels.sonnet?.model || resolvedModels.sonnet?.error || '–'}</code>
                                {' · '}
                                Opus → <code>{resolvedModels.opus?.model || resolvedModels.opus?.error || '–'}</code>
                              </>
                            )}
                          </p>
                        )}
                      </OptionRow>
                    </EngineAccount>
                  </Band>

                  <Band name="Builds" note={buildIntro('claude')}>
                    <OptionRows>
                      {/* Claude's build model is a maintainer's choice, not the
                    author's — the wizard stopped asking per build on 2026-08-01
                    and this is where the answer now lives. */}
                      <OptionRow
                        label="Build model"
                        hint={
                          claudeBuildModel === 'sonnet'
                            ? `Every new deck is written by Sonnet: quicker, and ${claudeAuth === 'api' ? 'several times cheaper per token' : 'lighter on the subscription'}. Opus writes the better deck.`
                            : 'Every new deck is written by Opus. Deck quality is the product, so this is the one to leave alone unless a build is taking too long.'
                        }
                      >
                        <Segmented
                          value={claudeBuildModel || 'opus'}
                          onChange={setClaudeBuildModel}
                          label="Claude build model"
                          options={[
                            { value: 'opus', label: 'Opus' },
                            { value: 'sonnet', label: 'Sonnet' },
                          ]}
                        />
                      </OptionRow>
                    </OptionRows>
                  </Band>

                  <Band name="Chat" note={chatIntro('claude')}>
                    <OptionRows>
                      {/* MODEL then EFFORT, the order every engine card reads in.
                    The row stays put when Sonnet is switched off rather than
                    vanishing — a row that disappears reads as a bug, and this
                    one still has an answer to state: Opus, on every turn. */}
                      <OptionRow
                        label="Chat model"
                        hint={
                          offerSonnet
                            ? 'Which one a message runs on when nobody picks.'
                            : 'Opus answers every message. Switch Sonnet on below to make this a choice.'
                        }
                      >
                        {offerSonnet ? (
                          <Segmented
                            value={claudeModel || 'opus'}
                            onChange={setClaudeModel}
                            label="Claude chat model"
                            options={[
                              { value: 'opus', label: 'Opus' },
                              { value: 'sonnet', label: 'Sonnet' },
                            ]}
                          />
                        ) : (
                          <span className="muted small">Opus</span>
                        )}
                      </OptionRow>

                      <OptionRow
                        label="Offer Sonnet"
                        sub
                        hint={
                          offerSonnet
                            ? 'Sonnet and Opus both appear in the chat model pill.'
                            : claudeAuth === 'api'
                              ? 'Sonnet is several times cheaper per token on the API, and weaker on edits.'
                              : 'On the subscription Sonnet is barely cheaper, no quicker, and weaker on edits.'
                        }
                      >
                        <Toggle checked={offerSonnet} onChange={setOfferSonnet} label="Offer Sonnet" />
                      </OptionRow>

                      <OptionRow
                        label="Thinking effort"
                        sub
                        stack
                        hint="How hard Claude thinks before editing. Chat only — deck builds always run at full effort."
                        more="It bites on open-ended asks (“make this punchier”), where Low measured around a third quicker. A precise ask lands at the same speed whatever this says."
                      >
                        <select
                          value={claudeChatEffort}
                          onChange={(e) => setClaudeChatEffort(e.target.value)}
                          aria-label="Claude chat thinking effort"
                        >
                          <option value="">Medium (default)</option>
                          <option value="low">Low — fastest</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="xhigh">Extra high</option>
                          <option value="max">Max — slowest</option>
                        </select>
                      </OptionRow>
                    </OptionRows>
                  </Band>
                </Pane>

                <Pane id="opencode" name="OpenCode" now={opencodeNow} active={section}>
                  <Band name="Account">
                    {/* OPENCODE — named for what it is since 2026-08-10. It was "Fast",
                  which described a speed rather than an engine and stopped being
                  true the moment Pi could run the same small models. One legal
                  account mode, so it states what it costs rather than offering a
                  dial, and one preset: the gateway, whose address happens to be
                  OpenCode Go here and can be any OpenAI-compatible endpoint. */}
                    <EngineAccount
                      name="OpenCode"
                      detail="OpenCode Go, OpenRouter, Groq…"
                      status={buildEngines.fast}
                      offered={fastEnabled}
                      onOffered={setFastEnabled}
                      offerCopy={{
                        on: 'OpenCode builds decks and answers messages on the gateway below. Turn it off to stop this server offering it at all — every turn and every build then goes to Claude or Pi.',
                        off: 'OpenCode is off for everyone on this server: it is not offered in the build wizard and the chat pill does not list it. The gateway key below is not spent.',
                      }}
                      mode={null}
                      modeCopy={null}
                      fixedCopy={
                        fastConfigured
                          ? 'Always metered: this engine has no subscription to run on. Charged to the gateway key below.'
                          : 'Always metered. Add a key and an address below to offer OpenCode in builds and chat.'
                      }
                      creds={[
                        {
                          id: 'gateway',
                          label: 'Gateway API key',
                          hint: 'Any OpenAI-compatible endpoint — OpenCode Go, OpenRouter, Groq, api.openai.com.',
                        },
                      ]}
                      credStatuses={creds}
                      onCredChange={mergeServerState}
                      extraRows={
                        <>
                          {/* ACCOUNT is the CONNECTION and nothing else — key and
                        address. Which model each surface runs on is stated in
                        the band that surface owns, so a card reads the same way
                        on every engine: Builds names the build model, Chat
                        names the chat one. */}
                          <OptionRow
                            label="Gateway address"
                            sub
                            stack
                            hint={
                              data.gatewayBaseUrl
                                ? `OpenCode is served from ${data.gatewayBaseUrl}. Blank falls back to LLM_GATEWAY_BASE_URL on the server.`
                                : 'No address set, so the gateway key has nothing to talk to. Paste the endpoint, e.g. https://opencode.ai/zen/go/v1.'
                            }
                          >
                            <input
                              type="url"
                              value={gatewayBaseUrl}
                              onChange={(e) => setGatewayBaseUrl(e.target.value)}
                              placeholder="https://opencode.ai/zen/go/v1"
                              aria-label="Gateway address"
                              spellCheck={false}
                            />
                          </OptionRow>
                        </>
                      }
                    />
                  </Band>

                  {fastEngine && (
                    <Band name="Builds" note={buildIntro('fast')}>
                      <OptionRows>
                        {/* OpenCode: its own model and rung since 2026-08-03. Blank on
                        either follows chat, which is what an OpenCode build always did. */}
                        <OptionRow
                          label="Build model"
                          stack
                          hint={
                            <>
                              {fastBuildModel ? (
                                <>
                                  An OpenCode build runs on <strong>{fastBuildModel}</strong>, whatever chat is set to.
                                </>
                              ) : (
                                <>
                                  An OpenCode build follows the chat OpenCode model
                                  {data?.fastChat?.model ? ` (${data.fastChat.model})` : ''}. Pick one here to give
                                  builds their own.
                                </>
                              )}
                              {gatewayModels?.models?.length
                                ? ` ${gatewayModels.models.length} listed by the gateway.`
                                : ''}
                            </>
                          }
                          /* A warning must always offer a way out, so this names
                         what still gets through rather than just objecting. */
                          note={
                            fastBuildSeesImages === false ? (
                              <p className="error small">
                                This model is text only, so it cannot read a diagram or screenshot in a brief. The
                                studio transcribes those at intake with the model set in Build quality, so the content
                                still reaches the build as text.
                              </p>
                            ) : null
                          }
                        >
                          {gatewayModels?.models?.length ? (
                            <select
                              value={fastBuildModel}
                              onChange={(e) => setFastBuildModel(e.target.value)}
                              aria-label="OpenCode build model"
                            >
                              <option value="">
                                Same as chat{data?.fastChat?.model ? ` (${data.fastChat.model})` : ''}
                              </option>
                              {/* A saved id the gateway has stopped listing stays pickable. */}
                              {fastBuildModel && !gatewayModels.models.includes(fastBuildModel) && (
                                <option value={fastBuildModel}>{fastBuildModel} (current)</option>
                              )}
                              {gatewayModels.models.map((m) => {
                                // Only ANNOTATE what we know. An unrecognised id
                                // gets its bare name rather than a guess.
                                const sees = readsImages(m);
                                return (
                                  <option key={m} value={m}>
                                    {m}
                                    {sees === true ? ' · reads images' : sees === false ? ' · text only' : ''}
                                  </option>
                                );
                              })}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={fastBuildModel}
                              onChange={(e) => setFastBuildModel(e.target.value)}
                              placeholder="Same as chat"
                              aria-label="OpenCode build model"
                              spellCheck={false}
                            />
                          )}
                        </OptionRow>

                        {fastBuildRungs.efforts.length > 0 && (
                          <OptionRow
                            label="Thinking effort"
                            sub
                            stack
                            hint="How hard the model thinks while it writes each slide. Its own dial, not the chat one."
                            more={
                              <>
                                <p>
                                  None is the rung worth having in chat and the last one you want writing a deck.
                                  Thinking is billed and capped as output, so a high rung on a long slide can cut the
                                  reply short.
                                </p>
                                <p>
                                  {fastBuildRungs.known
                                    ? `This model takes ${fastBuildRungs.efforts.length} rungs — the ones it does not list are coerced to one it does, so they are not offered.`
                                    : 'This model is not in our roster, so every rung the gateway accepts is offered and the gateway is the judge.'}
                                </p>
                              </>
                            }
                          >
                            <select
                              value={fastBuildEffort}
                              onChange={(e) => setFastBuildEffort(e.target.value)}
                              aria-label="OpenCode build thinking effort"
                            >
                              <option value="">The model&apos;s own default</option>
                              {fastBuildRungs.efforts.map((e) => (
                                <option key={e} value={e}>
                                  {effortLabel(e)}
                                </option>
                              ))}
                            </select>
                          </OptionRow>
                        )}
                        {/* A model that reasons with no settable rung gets no dial —
                        and a line saying so, because a row that vanishes reads
                        as a bug rather than an answer. */}
                        {fastBuildRungs.known && fastBuildRungs.efforts.length === 0 && (
                          <OptionRow
                            label="Thinking effort"
                            sub
                            stack
                            hint="This model thinks, but has no rung to set — the gateway does not take an effort for it, so builds run at whatever it does by default."
                          >
                            <span className="muted small">Not settable on this model</span>
                          </OptionRow>
                        )}
                      </OptionRows>
                    </Band>
                  )}

                  {/* CHAT names the model a message runs on, the way BUILDS above
                  names the build one — same two rows, same order, on every
                  engine card.
                  This band is NEVER gated on the gateway being configured, and
                  that is load-bearing rather than tidy: the model row used to
                  sit in Account precisely because Chat was hidden until
                  `llmStatus()` called the gateway configured, and THAT needs a
                  model. A server with a key and an address but no model could
                  reach no input that would give it one — every door needed the
                  thing behind it. Keeping the band always-rendered is what lets
                  the row live where a user looks for it. */}
                  <Band name="Chat" note={chatIntro('fast')}>
                    <OptionRows>
                      <OptionRow
                        label="Chat model"
                        stack
                        hint={
                          <>
                            What an OpenCode chat turn runs on, and what an OpenCode BUILD falls back to when the build
                            model above is left blank. Blank uses the <code>LLM_GATEWAY_MODEL</code> env.
                            {gatewayModels?.models?.length
                              ? ` ${gatewayModels.models.length} listed by the gateway.`
                              : ''}
                            {gatewayModels?.error ? ` ${gatewayModels.error}` : ''}
                          </>
                        }
                      >
                        {gatewayModels?.models?.length ? (
                          <select
                            value={gatewayModel}
                            onChange={(e) => setGatewayModel(e.target.value)}
                            aria-label="OpenCode chat model"
                          >
                            <option value="">From env (LLM_GATEWAY_MODEL)</option>
                            {/* A saved id the gateway has stopped listing stays pickable. */}
                            {gatewayModel && !gatewayModels.models.includes(gatewayModel) && (
                              <option value={gatewayModel}>{gatewayModel} (current)</option>
                            )}
                            {gatewayModels.models.map((m) => {
                              // Only ANNOTATE what we know — an unrecognised id
                              // gets its bare name rather than a guess.
                              const sees = readsImages(m);
                              return (
                                <option key={m} value={m}>
                                  {m}
                                  {sees === true ? ' · reads images' : sees === false ? ' · text only' : ''}
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={gatewayModel}
                            onChange={(e) => setGatewayModel(e.target.value)}
                            placeholder="e.g. kimi-k2 / deepseek-v4-flash"
                            aria-label="OpenCode chat model"
                            spellCheck={false}
                          />
                        )}
                      </OptionRow>

                      {gatewayChatRungs.efforts.length > 0 && (
                        <OptionRow
                          label="Thinking effort"
                          stack
                          hint="How hard the model thinks before it answers. The chat pill can dial one message down without changing this."
                          more={
                            <>
                              <p>
                                The big step is None versus anything else: on DeepSeek V4 Flash one slide rewrite took
                                about 7 seconds with None and 30 to 50 seconds on every thinking rung. Thinking is
                                billed and capped as output, so a high rung can truncate a long reply.
                              </p>
                              <p>
                                {gatewayChatRungs.known
                                  ? 'The rungs offered are this model’s own; the ones the gateway names for other models are coerced, so they are not listed here.'
                                  : 'This model is not in our roster, so every rung the gateway accepts is offered and the gateway is the judge.'}
                              </p>
                            </>
                          }
                        >
                          <select
                            value={gatewayEffort}
                            onChange={(e) => setGatewayEffort(e.target.value)}
                            aria-label="OpenCode chat thinking effort"
                          >
                            <option value="">The model&apos;s own default</option>
                            {gatewayChatRungs.efforts.map((e) => (
                              <option key={e} value={e}>
                                {effortLabel(e)}
                              </option>
                            ))}
                          </select>
                        </OptionRow>
                      )}
                      {gatewayChatRungs.known && gatewayChatRungs.efforts.length === 0 && (
                        <OptionRow
                          label="Thinking effort"
                          stack
                          hint="This model thinks, but has no rung to set — the gateway does not take an effort for it, so chat runs at whatever it does by default."
                        >
                          <span className="muted small">Not settable on this model</span>
                        </OptionRow>
                      )}
                    </OptionRows>
                  </Band>
                </Pane>

                <Pane id="pi" name="Pi" now={piNow} active={section}>
                  <Band name="Account">
                    {/* Pi, the one engine whose PROVIDER is a setting either way: on
                  the metered branch we assemble one from the endpoint, key and
                  wire format below; on a login it is whichever of Pi's own
                  OAuth providers is signed in on the server. The endpoint and
                  wire format therefore belong to the metered branch alone —
                  shown there because a key with nothing to talk to configures
                  nothing, and hidden on the login because they would describe a
                  provider the build never reaches. */}
                    <EngineAccount
                      name="Pi"
                      detail={piAuth === 'subscription' ? piSubscriptionProvider || 'subscription' : 'metered'}
                      status={buildEngines.pi}
                      offered={piEnabled}
                      onOffered={setPiEnabled}
                      offerCopy={{
                        on: 'Pi builds decks and answers messages on whichever provider it is pointed at below. Turn it off to stop this server offering it at all.',
                        off: 'Pi is off for everyone on this server: not offered in the build wizard, not listed by the chat pill. Neither the key nor the login below is spent.',
                      }}
                      mode={piAuth}
                      onMode={setPiAuth}
                      modeCopy={{
                        subscription: `Builds and chat run on the ${
                          piSubscriptionProvider || 'subscription'
                        } login signed in on this server, so nothing is charged to the key below. What that plan itself charges is between you and the provider — a Claude Pro/Max login bills as extra usage, an OpenRouter one draws down credits.`,
                        api: 'Builds and chat are charged to the Pi API key below, at whatever the endpoint you point it at charges.',
                      }}
                      apiCred={creds.pi}
                      creds={[
                        {
                          id: 'pi',
                          label: 'Pi API key',
                          // Says what it is FOR in the mode you are actually in. On
                          // the subscription branch this row is the way back to
                          // metered, not a thing to fill in now — and a key box under
                          // a subscription you have just chosen reads as a demand.
                          hint:
                            piAuth === 'subscription'
                              ? 'Not used while Pi runs on the login above. It is what the API key mode would spend, so a key here is only needed if you switch back.'
                              : 'Its own key, even when the endpoint is one another engine already uses: a provider trial must not quietly spend the chat budget.',
                        },
                      ]}
                      credStatuses={creds}
                      onCredChange={mergeServerState}
                      extraRows={
                        <>
                          {/* SAVED SETUPS, above everything they set. Comparing
                        providers is the reason this engine exists, and until
                        now it meant retyping the endpoint, the wire format and
                        the model every time you switched — and again to switch
                        back. A profile is a pure snapshot of the fields below,
                        so applying one just fills them in; there is no separate
                        source of truth and no active-profile pointer to go
                        stale (which one is current is derived by comparing). */}
                          <PiProfiles
                            profiles={piProfiles}
                            onProfiles={setPiProfiles}
                            current={{
                              auth: piAuth === 'subscription' ? 'subscription' : 'api',
                              subscriptionProvider: piSubscriptionProvider.trim() || null,
                              baseUrl: piBaseUrl.trim() || null,
                              api: piApi === 'openai-responses' ? 'openai-responses' : 'openai-completions',
                              model: piModel.trim() || null,
                            }}
                            onApply={(p) => {
                              // Every field, every time — including the ones this
                              // profile leaves blank. Applying a subscription profile
                              // over a metered one must not leave the old endpoint
                              // behind for the next switch back to pick up.
                              setPiAuth(p.auth);
                              setPiSubscriptionProvider(p.subscriptionProvider || '');
                              setPiBaseUrl(p.baseUrl || '');
                              setPiApi(p.api === 'openai-responses' ? 'openai-responses' : 'openai-completions');
                              setPiModel(p.model || '');
                            }}
                          />
                          {piAuth === 'subscription' ? (
                            // The provider field AND the sign-in flow, in one
                            // component: on this engine "which provider" and "is it
                            // signed in" are one question, and splitting them left the
                            // drawer able to name a login it could not mint.
                            <PiLogin
                              provider={piSubscriptionProvider}
                              onProvider={setPiSubscriptionProvider}
                              onChanged={refreshServerState}
                              issue={buildEngines.pi?.available === false ? buildEngines.pi?.issue : null}
                            />
                          ) : (
                            <>
                              <OptionRow
                                label="Endpoint"
                                sub
                                stack
                                hint={
                                  buildEngines.pi?.baseUrl
                                    ? `Pi builds talk to ${buildEngines.pi.baseUrl}. Blank falls back to PI_BASE_URL on the server. This is Pi's own address: it never moves the OpenCode gateway above.`
                                    : "No address set, so the Pi key has nothing to talk to. Paste the OpenAI-compatible endpoint, e.g. https://opencode.ai/zen/go/v1. This is Pi's own address: it never moves the OpenCode gateway above."
                                }
                              >
                                <input
                                  type="url"
                                  value={piBaseUrl}
                                  onChange={(e) => setPiBaseUrl(e.target.value)}
                                  placeholder="https://opencode.ai/zen/go/v1"
                                  aria-label="Pi endpoint"
                                  spellCheck={false}
                                />
                              </OptionRow>

                              <OptionRow
                                label="API style"
                                sub
                                stack
                                hint={
                                  apiProbe.issue
                                    ? apiProbe.issue
                                    : apiProbe.detail
                                      ? apiProbe.detail
                                      : piApi === 'openai-responses'
                                        ? "The Responses API. Pick this when the provider's own docs point at /v1/responses: it is the format the OpenCode engine cannot speak at all."
                                        : 'Chat completions, which nearly every OpenAI-compatible endpoint serves. Getting this wrong is fatal rather than degraded — the build dies on its first turn — so Test it rather than guessing.'
                                }
                              >
                                {/* The dropdown stays: the probe is a shortcut, not a
                              gate, and an endpoint the probe cannot reach must
                              still be configurable by hand. */}
                                <div className="cred">
                                  <select
                                    value={piApi}
                                    onChange={(e) => setPiApi(e.target.value)}
                                    aria-label="Pi API style"
                                  >
                                    <option value="openai-completions">Chat completions</option>
                                    <option value="openai-responses">Responses</option>
                                  </select>
                                  <button
                                    type="button"
                                    className="btn btn--sm btn--ghost"
                                    disabled={apiProbe.busy || !piBaseUrl.trim() || !piModel.trim()}
                                    onClick={probeApiStyle}
                                    title="Send one tiny request in each format and keep the one that answers"
                                  >
                                    {apiProbe.busy ? 'Testing…' : 'Test'}
                                  </button>
                                </div>
                              </OptionRow>
                            </>
                          )}
                        </>
                      }
                    />
                  </Band>

                  {buildEngines.pi && (
                    <>
                      <Band name="Builds" note={buildIntro('pi')}>
                        <OptionRows>
                          <OptionRow
                            label="Build model"
                            stack
                            hint={
                              piModel
                                ? `Pi builds run on ${piModel}, exactly as the endpoint names it. Free text on purpose: this engine exists so a new provider is a setting rather than a release, and no list we ship could keep up.`
                                : 'Name the model id the endpoint serves, e.g. deepseek-v4-flash. Pi cannot build without one, and an invented id fails at the provider rather than here.'
                            }
                          >
                            <PiModelField
                              value={piModel}
                              onChange={setPiModel}
                              models={piCatalogue}
                              placeholder="e.g. deepseek-v4-flash"
                              ariaLabel="Pi build model"
                            />
                            {/* SUBSCRIPTION ONLY: this asks the signed-in provider's
                                catalogue, and a metered Pi is pointed at an arbitrary
                                endpoint with nothing to ask. One button for all three
                                Pi model fields, because there is one catalogue behind
                                them and it is fetched once. */}
                            {piAuth === 'subscription' && (
                              <div className="cred">
                                <button
                                  type="button"
                                  className="btn btn--sm btn--ghost"
                                  disabled={piRefresh.busy}
                                  onClick={refreshPiModels}
                                  title="Ask the provider for models newer than the ones this pi install shipped with"
                                >
                                  {piRefresh.busy ? 'Refreshing…' : 'Refresh models'}
                                </button>
                                {piRefresh.issue ? (
                                  <span className="small error">{piRefresh.issue}</span>
                                ) : piRefresh.count !== null ? (
                                  <span className="small muted">
                                    Updated just now · {piRefresh.count} model{piRefresh.count === 1 ? '' : 's'}
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </OptionRow>

                          <OptionRow
                            label="Thinking effort"
                            sub
                            stack
                            hint={piRungHint(
                              piBuildRungs,
                              piModel || buildEngines.pi?.model || '',
                              piBuildEffort,
                              'Building a deck',
                            )}
                          >
                            <select
                              value={piBuildEffort}
                              onChange={(e) => setPiBuildEffort(e.target.value)}
                              aria-label="Pi build thinking effort"
                            >
                              <option value="">The model&apos;s own default</option>
                              {piBuildRungs.efforts.map((e) => (
                                <option key={e} value={e}>
                                  {effortLabel(e)}
                                </option>
                              ))}
                            </select>
                          </OptionRow>

                          <OptionRow
                            label="Context window"
                            sub
                            stack
                            hint={`How much context the endpoint serves, in tokens. Blank uses ${(buildEngines.pi.contextWindow || 256000).toLocaleString()}.`}
                            more="Pi reserves against this figure when it decides to compact a long build, so a number larger than the model really takes ends the build instead of compacting it."
                          >
                            <input
                              type="number"
                              min="8000"
                              step="1000"
                              value={piContextWindow}
                              onChange={(e) => setPiContextWindow(e.target.value)}
                              placeholder={String(buildEngines.pi.contextWindow || 256000)}
                              aria-label="Pi context window"
                            />
                          </OptionRow>

                          {/* Step 5 of the build skill is one writer per content section,
                        and this is whether Pi actually runs it that way. Each child
                        starts with an EMPTY context, so the hint says what the
                        parent has to hand over — that is the property that decides
                        whether the setting helps or hurts. It was default off while
                        children forked the parent's conversation (2-4.5x the cost
                        on the A/B ledger); they start fresh since 2026-08-10 and it
                        is default on. */}
                          <OptionRow
                            label="Section writers"
                            stack
                            hint={
                              piSubagents
                                ? 'Pi writes the content sections in parallel, one child agent per section (up to 8, 4 at a time) on the same key.'
                                : 'Off (default): Pi writes every slide itself, in one conversation that has read the brief and the plan.'
                            }
                            more={
                              piSubagents
                                ? "Each child starts with an empty context and gets a full written brief, which is what the build skill's step 5 describes. Anything a child fails to write, Pi writes itself."
                                : 'Measured 2026-09-08 on four builds of one brief: the writers-off decks were the coherent ones. Turn writers on for a very long deck where one conversation would compact away the plan.'
                            }
                          >
                            <Toggle checked={piSubagents} onChange={setPiSubagents} label="Section writers" />
                          </OptionRow>

                          {/* One slide rule, lifted for a model that draws well. The rule
                        ("Don't draw illustrations in SVG") exists because the models
                        it was written against drew crude shapes; a model that does
                        not deserves the choice, per build, without editing the rule
                        every other engine reads. Off by default. */}
                          <OptionRow
                            label="SVG illustrations"
                            stack
                            hint={
                              piSvgArt
                                ? 'Pi may draw inline SVG diagrams, infographics and schematics where a picture explains a how-to better than prose. The rest of the slide rules still apply.'
                                : 'Off: the slide rules stand, and Pi carries an idea with photographs, type and layout rather than drawing it.'
                            }
                            more="Turn it on for a model that draws well. Every Pi spawn of the build, the fresh-eyes review and the repair pass reads the same permission, so a drawing that keeps to the rules is never stripped as a fault."
                          >
                            <Toggle checked={piSvgArt} onChange={setPiSvgArt} label="SVG illustrations" />
                          </OptionRow>

                          {/* Only the Pi engine has this pass to switch off. Both hints
                        say what today's answer MEANS, so the off state reads as a
                        choice rather than a gap. */}
                          <OptionRow
                            label="Fresh-eyes review"
                            stack
                            hint={
                              piReviewPass
                                ? 'After a build, the server renders every slide and a fresh Pi session that did not write the deck looks at the pictures and fixes what a reader would notice. One pass, on the build model; if it cannot run, the build carries on.'
                                : "Off: the build goes straight to the server's own checks (overflow, repeated images, same-layout runs). Cheaper and faster, and nothing looks at the rendered slides."
                            }
                          >
                            <Toggle checked={piReviewPass} onChange={setPiReviewPass} label="Fresh-eyes review" />
                          </OptionRow>

                          {/* The reviewer's model, only while the review runs at all. Its
                        own row rather than a mode on the build model, because the
                        two jobs differ: the build WRITES the deck and the review
                        LOOKS at pictures of it, so a cheap text-only writer can
                        hand the renders to something with eyes. Same endpoint,
                        same key — never a second account. */}
                          {piReviewPass && (
                            <OptionRow
                              label="Review model"
                              sub
                              stack
                              hint={
                                piReviewModel ? (
                                  <>
                                    The fresh-eyes review looks at the rendered slides on{' '}
                                    <strong>{piReviewModel}</strong>
                                    {piReviewSeesImages === true
                                      ? ' (reads images)'
                                      : piReviewSeesImages === false
                                        ? ' (text only, so it would review from the source instead)'
                                        : ''}
                                    , at the build endpoint on the Pi key. If it cannot answer, the review runs on the
                                    build model instead and the build carries on.
                                  </>
                                ) : (
                                  <>
                                    Blank, so the review runs on the build model
                                    {piModel || buildEngines.pi?.model
                                      ? ` (${piModel || buildEngines.pi?.model})`
                                      : ''}{' '}
                                    — from the deck&apos;s source when that model cannot take images. Name a
                                    vision-capable model the endpoint serves (e.g. mimo-v2.5) and the review judges the
                                    rendered slides instead.
                                  </>
                                )
                              }
                            >
                              <PiModelField
                                value={piReviewModel}
                                onChange={setPiReviewModel}
                                models={piCatalogue}
                                allowBlank
                                blankLabel="Same as builds"
                                placeholder={piModel || buildEngines.pi?.model || 'Same as builds'}
                                ariaLabel="Pi review model"
                              />
                            </OptionRow>
                          )}

                          {/* The server's own eyes, only while the review runs at all.
                        It fires ONLY when the reviewing model is known to be
                        text-only — a reviewer with eyes keeps them, and an id
                        the roster has never heard of keeps the review's own
                        can-I-see self-test. Both hints say what today's answer
                        MEANS, so the off state reads as a choice. */}
                          {piReviewPass && (
                            <OptionRow
                              label="Server eyes"
                              sub
                              stack
                              hint={
                                piRenderDescribe
                                  ? 'When the reviewing model cannot read images, a vision model on the server looks at every render first, one slide at a time, and hands the review its findings as text.'
                                  : 'Off: a text-only reviewer reads the JSX instead of the renders — nothing looks at the pictures unless the review model itself can.'
                              }
                              more="Runs on the same gateway vision model that reads a brief's pictures, a call per slide (cents per build). Reviewers that can see images skip it, and a deploy with no gateway key falls back to the source review with a note in the transcript."
                            >
                              <Toggle checked={piRenderDescribe} onChange={setPiRenderDescribe} label="Server eyes" />
                            </OptionRow>
                          )}
                        </OptionRows>
                      </Band>

                      <Band name="Chat" note={chatIntro('pi')}>
                        <OptionRows>
                          <OptionRow
                            label="Chat model"
                            stack
                            hint={
                              piChatModel ? (
                                <>
                                  Messages run on <strong>{piChatModel}</strong>, whatever builds are set to. Same
                                  endpoint and same key either way — this engine has one of each.
                                </>
                              ) : (
                                <>
                                  Messages run on the same Pi model as builds
                                  {piModel || chatBlocks.pi?.model ? ` (${piModel || chatBlocks.pi?.model})` : ''}. Name
                                  one here to give chat its own — a lighter model for edits, say, while builds keep the
                                  deep one.
                                </>
                              )
                            }
                          >
                            <PiModelField
                              value={piChatModel}
                              onChange={setPiChatModel}
                              models={piCatalogue}
                              allowBlank
                              blankLabel="Same as builds"
                              placeholder={piModel || chatBlocks.pi?.model || 'Same as builds'}
                              ariaLabel="Pi chat model"
                            />
                          </OptionRow>

                          <OptionRow
                            label="Thinking effort"
                            sub
                            stack
                            hint={
                              <>
                                {piRungHint(
                                  piChatRungs,
                                  piChatModel || piModel || chatBlocks.pi?.model || '',
                                  // The EFFECTIVE rung, since a blank chat slot follows
                                  // the build one — saying "no rung is sent" while the
                                  // build dial is set would be the wrong half of the
                                  // chain.
                                  piChatEffort || piBuildEffort,
                                  'A message thinks',
                                )}{' '}
                                Blank follows the build rung{piBuildEffort ? ` (${piBuildEffort})` : ''}. The chat pill
                                can dial one message down without changing this.
                              </>
                            }
                          >
                            <select
                              value={piChatEffort}
                              onChange={(e) => setPiChatEffort(e.target.value)}
                              aria-label="Pi chat thinking effort"
                            >
                              <option value="">Same as builds{piBuildEffort ? ` (${piBuildEffort})` : ''}</option>
                              {piChatRungs.efforts.map((e) => (
                                <option key={e} value={e}>
                                  {effortLabel(e)}
                                </option>
                              ))}
                            </select>
                          </OptionRow>
                        </OptionRows>
                      </Band>
                    </>
                  )}
                </Pane>

                {/* BUILD QUALITY — the two build settings that belong to no engine:
                one runs at intake, before an engine is even chosen, and the
                other reports on the deck afterwards, whichever engine wrote
                it. Under an engine's card either would read as that engine's
                setting, which is how the fill slider spent a release looking
                like a Pi one. */}
                <Pane id="quality" name="Build quality" now={qualityNow} active={section}>
                  <p className="small">Applies to every build, whichever engine runs it.</p>

                  <OptionRows>
                    {/* THE FALLBACK for reading a brief's pictures — no longer the
                    reader itself. Until 2026-08-19 every picture the server read
                    went through this gateway model whichever engine was
                    building, which tied the studio's eyes to a credential that
                    is going away (Jake, 2026-08-19). The build engine's own
                    model reads them now; this slot only answers when that model
                    cannot see, or when no engine is configured. Only
                    vision-capable ids are listed — a text-only one here would
                    silently undo the fallback too. */}
                    <OptionRow
                      label="Fallback image reader"
                      hint={
                        <>
                          The build engine reads the pictures in a brief with its own model. This gateway model is used
                          only when that model cannot see images.
                        </>
                      }
                      more={
                        <>
                          <p>
                            A brief&apos;s content is sometimes a picture — a process diagram pasted into a Word doc,
                            which arrives as an image and nothing else. It is transcribed into the brief text at intake
                            so the words are on disk to check, rather than trapped in one model&apos;s context.
                          </p>
                          <p>
                            Whoever builds the deck does that reading: Pi and Claude look at the pictures through their
                            own CLI, on the credential you already pay for. This slot is the fallback for a build model
                            the roster knows is text-only, and it is the only one of the three that needs a gateway key.
                            {visionModelIds.length ? ` ${visionModelIds.length} gateway models can read images.` : ''}
                          </p>
                        </>
                      }
                    >
                      {visionModelIds.length ? (
                        <select
                          value={briefImageModel}
                          onChange={(e) => setBriefImageModel(e.target.value)}
                          aria-label="Fallback image reader"
                        >
                          <option value="">Default (mimo-v2.5)</option>
                          {/* A saved id that has stopped being listed stays pickable
                          rather than silently resetting to the default. */}
                          {briefImageModel && !visionModelIds.includes(briefImageModel) && (
                            <option value={briefImageModel}>{briefImageModel} (current)</option>
                          )}
                          {visionModelIds.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={briefImageModel}
                          onChange={(e) => setBriefImageModel(e.target.value)}
                          placeholder="Default (mimo-v2.5)"
                          aria-label="Fallback image reader"
                          spellCheck={false}
                        />
                      )}
                    </OptionRow>

                    <OptionRow
                      label="Slide fill"
                      stack
                      hint={
                        fillFloor === 0
                          ? 'Off: a build never reports empty-looking slides.'
                          : `A build lists slides whose content covers less than ${Math.round(fillFloor * 100)}% of the frame, so you can look at them. It never changes a slide — a statement or a big stat is meant to read spare.`
                      }
                    >
                      <input
                        type="range"
                        min="0"
                        max="0.6"
                        step="0.05"
                        value={fillFloor}
                        onChange={(e) => setFillFloor(Number(e.target.value))}
                        aria-label="Whitespace floor: flag slides emptier than this"
                      />
                    </OptionRow>
                  </OptionRows>
                </Pane>

                <Pane id="media" name="Stock media" now={mediaNow} active={section}>
                  <p className="small">
                    Photos:{' '}
                    {data.media?.unsplash ? (
                      <>
                        Unsplash{' '}
                        <span className="muted">
                          <Check className="icon-inline" size={12} /> key set
                        </span>
                      </>
                    ) : data.media?.pexels ? (
                      <>
                        Pexels{' '}
                        <span className="muted">
                          <Check className="icon-inline" size={12} /> key set
                        </span>
                      </>
                    ) : (
                      <span className="muted">placeholder images only (no key)</span>
                    )}
                    {' · '}
                    Video:{' '}
                    {data.media?.pexels ? (
                      <>
                        Pexels{' '}
                        <span className="muted">
                          <Check className="icon-inline" size={12} /> key set
                        </span>
                      </>
                    ) : (
                      <span className="muted">unavailable (set PEXELS_API_KEY)</span>
                    )}
                  </p>
                </Pane>

                <Pane id="storage" name="Storage" active={section}>
                  <p className="small muted">
                    Files the studio can always make again. Clearing them frees disk and costs only the time to rebuild
                    whichever you clear.
                  </p>
                  <StoragePanel />
                </Pane>

                <Pane id="people" name="People" now="who can sign in" active={section}>
                  <PeoplePanel />
                </Pane>

                <Pane id="usage" name="Usage costs" active={section}>
                  <p className="small muted">
                    What each deck spent: every build, repair and chat turn writes a row with its model, tokens and
                    cost. Metered is a real bill; subscription-notional is what the plan absorbed.
                  </p>
                  <UsagePanel />
                </Pane>

                <Pane
                  id="running"
                  name="Running builds"
                  now={buildList === null ? '' : runningBuilds === 0 ? 'none' : `${runningBuilds} running`}
                  active={section}
                >
                  <p className="small muted">
                    Headless deck builds running on the server. A build counts against the concurrency limit until it
                    finishes; cancel a stuck one here to free the new-deck wizard.
                  </p>
                  <BuildsPanel />
                </Pane>
              </div>

              {/* A REAL FOOTER, outside the scroller (2026-08-11). It was a
                `position: sticky` row inside it, with a negative bottom margin
                cancelling the scroller's padding — and a bottom-stuck element
                is offset by its MARGIN box, so the negative margin lifted the
                bar a clear 16px off the bottom edge and left a strip of
                content scrolling past underneath it. Nothing to get wrong once
                the bar is a flex sibling of the scroll region rather than a
                child of it. */}
              {/* `data-dirty` is the flag itself, not decoration: it is the only
                way to assert this from outside, and a cue whose correctness
                cannot be checked is the kind that quietly starts lying. */}
              <div className="settings__actions settings-console__foot" data-dirty={dirty ? 'true' : 'false'}>
                {saveButton}
                {/* Named before the generic states: it is the one reason Save is
                  refusing, and it says which engine and what to do about it. */}
                {saveBlock && <span className="error small">{saveBlock}</span>}
                {/* Says what is true of the drawer right now, in the one place
                  that is always on screen — the actions row is sticky. It
                  outranks "Saved", which otherwise sat there being wrong for as
                  long as someone kept editing after a save. */}
                {!saveBlock && dirty && (
                  <span className="settings__unsaved small">Not saved yet: press Save to apply these changes.</span>
                )}
                {!saveBlock && !dirty && saved && (
                  <span className="muted small">Saved: applies from the next message.</span>
                )}
                {!saveBlock && error && <span className="error small">{error}</span>}
              </div>
            </div>
          </div>
        )}
        {!data && error && <p className="error small">{error}</p>}
      </Drawer>
    </>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Per-deck totals from the server's usage ledger (GET /api/usage/report).
 *  Only the fields this panel reads — the full line shape stays server-side. */
type UsageReportPayload = {
  perDeck: Array<{
    slug: string;
    calls: number;
    inputTokens: number;
    outputTokens: number;
    meteredUsd: number | null;
    subscriptionUsd: number | null;
  }>;
  totals: { calls: number; meteredUsd: number | null; subscriptionUsd: number | null };
  lines: Array<{ slug: string; lastTs: string }>;
};

/**
 * The cost report, readable in place and downloadable as CSV. Fetched on
 * open (the Group mounts children only while open, same as Storage) — this is
 * a ledger read, not something to poll. Money is shown split: metered is a
 * bill, subscription-notional is what the plan absorbed, and adding them
 * together would mislead in both directions.
 */
function UsagePanel() {
  const [report, setReport] = useState<UsageReportPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<UsageReportPayload>('/api/usage/report')
      .then(setReport)
      .catch((err) => setError(errorMessage(err)));
  }, []);

  if (error) return <p className="error small">{error}</p>;
  if (report === null) return <p className="muted small">Reading the ledger…</p>;
  if (!report.perDeck.length) return <p className="muted small">No usage recorded yet.</p>;

  const money = (v: number | null) => (v == null ? '—' : `$${v.toFixed(2)}`);
  // Most recently active decks first, using each deck's newest ledger row.
  const lastActive = new Map<string, string>();
  for (const l of report.lines) {
    const prev = lastActive.get(l.slug);
    if (!prev || l.lastTs > prev) lastActive.set(l.slug, l.lastTs);
  }
  const recent = [...report.perDeck]
    .sort((a, b) => (lastActive.get(b.slug) || '').localeCompare(lastActive.get(a.slug) || ''))
    .slice(0, 6);

  return (
    <div className="settings__usage">
      <p className="small">
        {report.totals.calls} runs recorded · metered {money(report.totals.meteredUsd)} · subscription-notional{' '}
        {money(report.totals.subscriptionUsd)}
      </p>
      <table className="small" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {recent.map((d) => (
            <tr key={d.slug}>
              <td style={{ padding: '0.15rem 0', overflowWrap: 'anywhere' }}>{d.slug}</td>
              <td style={{ padding: '0.15rem 0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }} className="muted">
                {d.calls} run{d.calls === 1 ? '' : 's'}
              </td>
              <td style={{ padding: '0.15rem 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {d.meteredUsd != null ? `${money(d.meteredUsd)} metered` : money(d.subscriptionUsd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="small" style={{ marginTop: '0.5rem' }}>
        <a className="btn btn--sm" href="/api/usage/report?format=csv" download>
          Download full report (CSV)
        </a>
      </p>
    </div>
  );
}

/**
 * Regenerable files, and a button to delete them. Every row here can be
 * rebuilt from a deck's source — clearing one costs time, never content — so
 * the row says what it will cost rather than asking the user to know.
 *
 * Debug renders are pre-ticked because nothing reads them at all after the
 * build that wrote them; the other two are opt-in because their absence is
 * something you'd notice (a slow first look at a deck, a rebuild before the
 * next publish). Sizes are measured on open, not polled — this is a housekeeping
 * panel, and a walk of every deck directory has no business running on a timer.
 */
function StoragePanel() {
  const [caches, setCaches] = useState<CacheUsage[] | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<CacheKind>>(() => new Set<CacheKind>(['renders']));
  const [busy, setBusy] = useState(false);
  const [freed, setFreed] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      const d = await api.get<CacheUsagePayload>('/api/settings/cache-usage');
      setCaches(d.caches || []);
      setError(d.error || null);
    } catch (err) {
      setCaches([]);
      setError(errorMessage(err));
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  function toggle(kind: CacheKind) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  async function clear() {
    setBusy(true);
    setFreed(null);
    setError(null);
    try {
      const d = await api.post<ClearCachePayload>('/api/settings/clear-cache', { kinds: [...picked] });
      setFreed(d.bytes || 0);
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (caches === null) return <p className="muted small">Measuring…</p>;

  const selected = caches.filter((c) => picked.has(c.kind));
  const total = selected.reduce((n, c) => n + c.bytes, 0);
  const nothingToDo = total === 0;

  return (
    <div className="settings__storage">
      {caches.map((c) => (
        <label key={c.kind} className="settings__provider">
          <input type="checkbox" checked={picked.has(c.kind)} onChange={() => toggle(c.kind)} />
          <span>
            {c.label}{' '}
            <span className="muted">
              ({formatBytes(c.bytes)}
              {c.decks ? ` · ${c.decks} deck${c.decks === 1 ? '' : 's'}` : ''})
            </span>
            <span className="small muted"> {c.note}</span>
          </span>
        </label>
      ))}
      <div className="settings__field">
        <button type="button" className="btn btn--ghost btn--sm" disabled={busy || nothingToDo} onClick={clear}>
          {busy ? 'Clearing…' : nothingToDo ? 'Nothing to clear' : `Clear ${formatBytes(total)}`}
        </button>
        {freed !== null && <span className="small muted">Freed {formatBytes(freed)}.</span>}
        {error && <span className="error small">{error}</span>}
      </div>
    </div>
  );
}

/** Live list of server-side build jobs with a per-job kill button, on the
 *  shared poller (lib/useBuilds.js). Cancelling an ongoing or stuck build lives
 *  here — a job stuck as `running` holds a build slot for an hour.
 *
 *  It is no longer the ONLY place a running build is visible: the top-bar
 *  BuildsIndicator lists in-flight builds on every route and links to each
 *  one's watch page. This panel keeps what that one deliberately doesn't have —
 *  terminal jobs, and the kill button. */
function BuildsPanel() {
  const { builds, running, maxConcurrent, error } = useBuilds();
  const [killError, setKillError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function cancel(id: string) {
    setBusyId(id);
    setKillError(null);
    try {
      await killBuild(id);
    } catch (err) {
      setKillError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  if (builds === null) return error ? <p className="error small">{error}</p> : <p className="muted small">Loading…</p>;

  const active = builds.filter(isActiveBuild);

  return (
    <div className="settings__builds">
      {/* A banner, never a replacement: one failed 3s poll used to wipe the
          whole panel — list, slot count and Kill buttons — which is the one
          thing you need when a build is stuck. */}
      {(error || killError) && <p className="error small">{killError || error}</p>}
      <p className="small muted">
        {running} of {maxConcurrent} build slot{maxConcurrent === 1 ? '' : 's'} in use.
      </p>
      {builds.length === 0 && <p className="muted small">No builds: the wizard is free.</p>}
      {builds.length > 0 && (
        <ul className="settings__build-list">
          {builds.map((b) => (
            <li key={b.id} className="settings__build">
              <div className="settings__build-info">
                <span className="settings__build-slug">{b.slug || b.id}</span>
                <span className="muted small">
                  {b.engine} · {b.status}
                  {b.phase ? ` · ${b.phase}` : ''}
                  {typeof b.slides === 'number' ? ` · ${b.slides} slide${b.slides === 1 ? '' : 's'}` : ''}
                  {' · '}
                  {formatBuildAge(b.startedAt)}
                </span>
              </div>
              {isActiveBuild(b) && (
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  onClick={() => cancel(b.id)}
                  disabled={busyId === b.id}
                >
                  {busyId === b.id ? 'Killing…' : 'Kill'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {active.length === 0 && builds.length > 0 && <p className="muted small">No active builds to cancel.</p>}
    </div>
  );
}
